import React, { useState, useMemo, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const APP_PASSWORD = "curtainideas";
const ADMIN_PASSWORD = "curtainadmin";

const cleanData = (data) => {
  const cleaned = { ...data };
  ["first_contact", "next_contact", "cm_date", "quote_sent_date", "approved_date", "rejected_date"].forEach((k) => {
    if (cleaned[k] === "" || cleaned[k] === undefined) cleaned[k] = null;
  });
  if (cleaned.quote_value === "" || cleaned.quote_value === undefined) cleaned.quote_value = null;
  if (cleaned.invoice_value === "" || cleaned.invoice_value === undefined) cleaned.invoice_value = null;
  if (Array.isArray(cleaned.notes)) cleaned.notes = JSON.stringify(cleaned.notes);
  return cleaned;
};

const query = async (method, body) => {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/leads${method}`, {
    method: body ? (method.includes("id=") ? "PATCH" : "POST") : "GET",
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  return res.json();
};

const supabase = {
  getAll: async () => {
    const res = await query("?order=created_at.desc");
    return res.map(l => ({ ...l, notes: l.notes ? (typeof l.notes === "string" ? JSON.parse(l.notes) : l.notes) : [] }));
  },
  insert: (data) => query("", cleanData(data)),
  update: (id, data) => { const { id: _, created_at, ...rest } = data; return query(`?id=eq.${id}`, cleanData(rest)); },
  delete: async (id) => {
    await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${id}`, {
      method: "DELETE",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
  },
};

// Fallback defaults — overridden by Supabase settings at runtime
const DEFAULT_SOURCES = ["Referral", "Previous Customer", "Facebook", "Website Form", "Phone Call", "Walk In", "Shows & Exhibition", "Trade Customer", "Call & Online", "Trade & Repeat"];
const DEFAULT_WARMTH = ["", "Unlikely", "Likely", "Verbally Approved"];
const DEFAULT_STATUS = ["In Progress", "Approved", "Rejected", "Uncontactable", "Delayed"];
const DEFAULT_TEAM_MEMBERS = ["Carole", "Barb", "Sandi", "Dee", "Andy"];
const DEFAULT_PRODUCTS = [
  {key:"curtains", label:"Curtains", suppliers:["Curtain Ideas","Other"]},
  {key:"tracks",   label:"Tracks",   suppliers:["Track Co","Track Star","Other"]},
  {key:"blinds",   label:"Blinds",   suppliers:["In Line","Sunscreen Solutions","NBS","Other"]},
  {key:"shutters", label:"Shutters", suppliers:["Shutter Solutions","Other"]},
  {key:"awnings",  label:"Awnings",  suppliers:["Other"]},
  {key:"other",    label:"Other",    suppliers:["Other"]},
];
const DEFAULT_COMPONENTS = [
  {key:"fabric",      label:"Fabric",       products:["curtains"]},
  {key:"drapes",      label:"Drapes",       products:["curtains"]},
  {key:"sheers",      label:"Sheers",       products:["curtains"]},
  {key:"eyelets",     label:"Eyelets",      products:["curtains"]},
  {key:"pelmet",      label:"Pelmet",       products:["curtains"]},
  {key:"tbacks",      label:"T/Backs",      products:["curtains"]},
  {key:"tracks",      label:"Tracks",       products:["curtains","tracks"]},
  {key:"roller",      label:"B/O Roller",   products:["blinds"]},
  {key:"sscreen",     label:"Screen Roller",products:["blinds"]},
  {key:"venetian",    label:"Venetian",     products:["blinds"]},
  {key:"roman",       label:"Roman",        products:["blinds"]},
  {key:"shutters_cm", label:"Shutters CM",  products:["shutters"]},
  {key:"other",       label:"Other",        products:["curtains","blinds","shutters","awnings","other"]},
];


const WARMTH_COLORS = {
  "Unlikely":          { bg: "#FEF2F2", text: "#991B1B" },
  "Likely":            { bg: "#FFFBEB", text: "#B45309" },
  "Verbally Approved": { bg: "#F2F9E7", text: "#5A8A1A" },
};

const formatCurrency = (val) => val != null && val !== "" ? `$${Number(val).toLocaleString("en-AU")}` : "—";
const formatDate = (d) => { if (!d) return "—"; const [y, m, day] = d.split("-"); return `${day}/${m}/${y}`; };

const now = new Date();
const thisYear = now.getFullYear();
const thisMonth = now.getMonth();

const isMTD = (dateStr) => { if (!dateStr) return false; const d = new Date(dateStr); return d.getFullYear() === thisYear && d.getMonth() === thisMonth; };

const addBusinessDays = (date, days) => {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return result;
};

const toDateOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const getContactUrgency = (nextContactStr) => {
  if (!nextContactStr) return null;
  const today = toDateOnly(new Date());
  const contact = toDateOnly(new Date(nextContactStr));
  const limit = toDateOnly(addBusinessDays(new Date(), 2));
  if (contact < today) return "overdue";
  if (contact.getTime() === today.getTime()) return "today";
  if (contact <= limit) return "upcoming";
  return null;
};

const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 };
const inputStyle = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 14, color: "#0F172A", fontFamily: "inherit", background: "#FAFAFA", boxSizing: "border-box", outline: "none" };

const WarmthBadge = ({ warmth }) => {
  const c = WARMTH_COLORS[warmth] || { bg: "#F1F5F9", text: "#64748B" };
  return warmth ? <span style={{ background: c.bg, color: c.text, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{warmth}</span> : null;
};

const StatCard = ({ label, value, color, sub }) => (
  <div style={{ background: "#fff", borderRadius: 12, padding: "20px 22px", border: "1px solid #E2E8F0" }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>{sub}</div>}
  </div>
);

const emptyLead = () => ({
  name: "", email: "", phone: "",
  street: "", suburb: "", postcode: "",
  source: DEFAULT_SOURCES[0], status: DEFAULT_STATUS[0], warmth: "",
  first_contact: new Date().toISOString().split("T")[0],
  next_contact: "", quote_value: "", quote_number: "", book_number: "",
  cm_date: "", cm_time: "", cm_assignee: "", cm_notes: "", cm_record_id: "", quote_sent_date: "", approved_date: "", rejected_date: "", invoice_number: "", invoice_value: "", brief_note: "", detailed_note: "", notes: [],
});

// ─── Notes Editor ────────────────────────────────────────────────────────────
const NotesEditor = ({ notes, onChange, compact }) => {
  const [newText, setNewText] = useState("");
  const [newDate, setNewDate] = useState(new Date().toISOString().split("T")[0]);
  const { teamMembers: TEAM_MEMBERS } = useSettings() || { teamMembers: DEFAULT_TEAM_MEMBERS };
  const [newAuthor, setNewAuthor] = useState((DEFAULT_TEAM_MEMBERS)[0]);

  const addNote = () => {
    if (!newText.trim()) return;
    const updated = [{ id: Date.now(), date: newDate, author: newAuthor, text: newText.trim() }, ...notes];
    onChange(updated);
    setNewText("");
    setNewDate(new Date().toISOString().split("T")[0]);
  };

  const deleteNote = (id) => onChange(notes.filter((n) => n.id !== id));

  const sorted = [...notes].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div style={{ gridColumn: "1/-1" }}>
      {/* Add new note */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
          style={{ ...inputStyle, width: 145, flexShrink: 0 }} />
        <select value={newAuthor} onChange={(e) => setNewAuthor(e.target.value)}
          style={{ ...inputStyle, width: 110, flexShrink: 0 }}>
          {TEAM_MEMBERS.map(m => <option key={m}>{m}</option>)}
        </select>
        <textarea value={newText} onChange={(e) => setNewText(e.target.value)}
          placeholder="Write a note..."
          rows={compact ? 2 : 3}
          onKeyDown={(e) => { if (e.key === "Enter" && e.metaKey) addNote(); }}
          style={{ ...inputStyle, flex: 1, minWidth: 180, resize: "vertical", lineHeight: 1.5 }} />
        <button onClick={addNote}
          style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, flexShrink: 0, height: 40 }}>
          + Add
        </button>
      </div>
      {/* Existing notes */}
      {sorted.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: compact ? 180 : 280, overflowY: "auto" }}>
          {sorted.map((note) => (
            <div key={note.id} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ flexShrink: 0, paddingTop: 1, minWidth: 90 }}>
                <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600 }}>
                  {note.date ? new Date(note.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                </div>
                {note.author && <div style={{ fontSize: 11, color: "#8DC73F", fontWeight: 700, marginTop: 2 }}>{note.author}</div>}
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#334155", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{note.text}</div>
              <button onClick={() => deleteNote(note.id)}
                style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: "#CBD5E1", fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



const storage = {
  upload: async (leadId, file, displayName) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop() : "";
    const safeName = (displayName || file.name).replace(/[^a-zA-Z0-9._\- ]/g, "_");
    const fileName = ext && !safeName.endsWith("." + ext) ? safeName + "." + ext : safeName;
    const path = leadId + "/" + Date.now() + "_" + fileName;
    const res = await fetch(SUPABASE_URL + "/storage/v1/object/lead-files/" + path, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: "Bearer " + SUPABASE_KEY, "Content-Type": file.type },
      body: file,
    });
    if (!res.ok) throw new Error("Upload failed");
    return path;
  },
  list: async (leadId) => {
    const res = await fetch(SUPABASE_URL + "/storage/v1/object/list/lead-files", {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: "Bearer " + SUPABASE_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ prefix: leadId + "/", limit: 100 }),
    });
    if (!res.ok) return [];
    return res.json();
  },
  getSignedUrl: async (path) => {
    const res = await fetch(SUPABASE_URL + "/storage/v1/object/sign/lead-files/" + path, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: "Bearer " + SUPABASE_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ expiresIn: 3600 }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return SUPABASE_URL + "/storage/v1" + data.signedURL;
  },
  delete: async (path) => {
    await fetch(SUPABASE_URL + "/storage/v1/object/lead-files/" + path, {
      method: "DELETE",
      headers: { apikey: SUPABASE_KEY, Authorization: "Bearer " + SUPABASE_KEY },
    });
  },
};

// ─── File Uploader ───────────────────────────────────────────────────────────
const FileUploader = ({ leadId, refreshTrigger }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const loadFiles = async () => {
    if (!leadId) return;
    try {
      const data = await storage.list(leadId);
      setFiles(Array.isArray(data) ? data : []);
    } catch { setFiles([]); }
  };

  useEffect(() => { loadFiles(); }, [leadId, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    e.target.value = "";
    setUploading(true);
    setError(null);
    try {
      for (const file of selected) {
        const defaultName = file.name.replace(/\.[^.]+$/, "");
        const displayName = window.prompt("Name this file:", defaultName);
        if (displayName === null) continue; // user cancelled
        const name = displayName.trim() || defaultName;
        await storage.upload(leadId, file, name);
        await activityApi.log(leadId, getCurrentUser(), "File uploaded", name);
      }
      await loadFiles();
    } catch { setError("Upload failed. Please try again."); }
    setUploading(false);
  };

  const handleDelete = async (path) => {
    if (!window.confirm("Delete this file?")) return;
    await storage.delete(path);
    await loadFiles();
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const getIcon = (name) => {
    const ext = (name.split(".").pop() || "").toLowerCase();
    if (["jpg","jpeg","png","gif","webp"].includes(ext)) return "🖼️";
    if (ext === "pdf") return "📄";
    if (["doc","docx"].includes(ext)) return "📝";
    if (["xls","xlsx","csv"].includes(ext)) return "📊";
    return "📎";
  };

  if (!leadId) return (
    <div style={{ border: "2px dashed #E2E8F0", borderRadius: 10, padding: "20px", textAlign: "center", background: "#FAFAFA" }}>
      <div style={{ fontSize: 13, color: "#94A3B8" }}>Save the lead first to enable file uploads</div>
    </div>
  );

  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: "#F2F9E7", border: "1px solid #C6E68A", borderRadius: 8, cursor: uploading ? "not-allowed" : "pointer", width: "fit-content", marginBottom: 12, opacity: uploading ? 0.6 : 1 }}>
        <span style={{ fontSize: 15 }}>📎</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#5A8A1A" }}>{uploading ? "Uploading…" : "Upload Files"}</span>
        <input type="file" multiple onChange={handleUpload} disabled={uploading} style={{ display: "none" }} />
      </label>
      {error && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 8 }}>{error}</div>}
      {files.length === 0 ? (
        <div style={{ fontSize: 13, color: "#CBD5E1", textAlign: "center", padding: "16px 0" }}>No files uploaded yet</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((file) => {
            const path = leadId + "/" + file.name;
            const displayName = file.name.replace(/^[0-9]+_/, "");
            return (
              <div key={file.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{getIcon(file.name)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button onClick={async () => { const url = await storage.getSignedUrl(path); if (url) window.open(url, "_blank"); }}
                    style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", textDecoration: "none", background: "none", border: "none", padding: 0, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit" }}>
                    {displayName}
                  </button>
                  {file.metadata?.size && <div style={{ fontSize: 11, color: "#94A3B8" }}>{formatSize(file.metadata.size)}</div>}
                </div>
                <button onClick={() => handleDelete(path)}
                  style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: "#CBD5E1", fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};



// ─── Settings API ─────────────────────────────────────────────────────────────
const settingsApi = {
  getAll: async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/settings?select=*`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
    const rows = await res.json();
    const map = {};
    (rows || []).forEach(r => { map[r.key] = r.value; });
    return map;
  },
  set: async (key, value) => {
    await fetch(`${SUPABASE_URL}/rest/v1/settings?key=eq.${key}`, {
      method: "PATCH",
      headers: {
        apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`,
        "Content-Type": "application/json", Prefer: "return=representation",
      },
      body: JSON.stringify({ value }),
    });
  },
};

// Settings context — loaded once at app start, passed down
const SettingsContext = React.createContext(null);
const useSettings = () => React.useContext(SettingsContext);



// ─── Mobile CSS ───────────────────────────────────────────────────────────────
typeof document !== "undefined" && (() => {
  const s = document.createElement("style");
  s.textContent = `
    * { box-sizing: border-box; }
    @media (max-width: 768px) {
      /* Layout */
      .ci-page-wrap { padding: 12px !important; }
      .ci-topbar { padding: 0 12px !important; }

      /* Nav */
      .ci-nav { padding: 0 4px !important; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
      .ci-nav::-webkit-scrollbar { display: none; }
      .ci-nav button { padding: 12px 10px !important; font-size: 12px !important; white-space: nowrap; }

      /* Dashboard stats */
      .ci-stat-grid { grid-template-columns: repeat(2, 1fr) !important; }

      /* Leads table — scroll horizontally */
      .ci-leads-table-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
      .ci-leads-table { font-size: 12px !important; min-width: 640px; }
      .ci-leads-table td, .ci-leads-table th { padding: 9px 8px !important; }

      /* WIP table — scroll horizontally */
      .ci-wip-table-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
      .ci-wip-table { min-width: 600px; }

      /* Lead modal (new/edit) */
      .ci-new-modal { grid-template-columns: 1fr !important; padding: 16px !important; }
      .ci-modal-two-col { grid-template-columns: 1fr !important; height: auto !important; max-height: 94vh !important; overflow-y: auto !important; }
      .ci-modal-left { border-right: none !important; border-bottom: 1px solid #F1F5F9 !important; max-height: none !important; overflow-y: visible !important; }
      .ci-modal-right { height: 60vh !important; }
      .ci-modal-header { padding: 16px 16px 12px !important; }
      .ci-modal-header-actions { flex-wrap: wrap; gap: 8px !important; }

      /* Quick modal */
      .ci-quick-modal { max-width: calc(100vw - 16px) !important; margin: 8px !important; }

      /* WIP modal — sheet from bottom */
      .ci-wip-wrap { align-items: flex-end !important; padding: 0 !important; }
      .ci-wip-modal { max-width: 100% !important; width: 100% !important; margin: 0 !important; border-radius: 16px 16px 0 0 !important; max-height: 94vh !important; }
      .ci-wip-order-row { flex-wrap: wrap !important; gap: 12px !important; }

      /* Admin */
      .ci-admin-grid { grid-template-columns: 1fr !important; }

      /* Components */
      .ci-comp-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important; }

      /* Prevent any element from causing horizontal scroll */
      .ci-page-wrap > * { max-width: 100%; }
      input, select, textarea, button { max-width: 100%; }
    }
  `;
  document.head.appendChild(s);
})();

// ─── Activity Log API ─────────────────────────────────────────────────────────
const activityApi = {
  log: async (leadId, userName, event, detail) => {
    await fetch(`${SUPABASE_URL}/rest/v1/activity_log`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json" },
      body: JSON.stringify({ lead_id: leadId, user_name: userName, event, detail }),
    });
  },
  getForLead: async (leadId) => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/activity_log?lead_id=eq.${leadId}&order=created_at.desc&limit=50`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
    return res.json();
  },
};

// Current user — stored in sessionStorage
const getCurrentUser = () => sessionStorage.getItem("ci_user") || "";
const setCurrentUser = (name) => sessionStorage.setItem("ci_user", name);


// ─── Partners API ─────────────────────────────────────────────────────────────
const partnersApi = {
  getAll: async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/partners?order=company_name.asc`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
    const data = await res.json();
    return Array.isArray(data) ? data.map(p => ({ ...p,
      notes: p.notes ? (typeof p.notes === "string" ? JSON.parse(p.notes) : p.notes) : [],
      referrals: p.referrals ? (typeof p.referrals === "string" ? JSON.parse(p.referrals) : p.referrals) : [],
    })) : [];
  },
  insert: async (data) => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/partners`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json", Prefer: "return=representation" },
      body: JSON.stringify(data),
    });
    const result = await res.json();
    return Array.isArray(result) ? result[0] : result;
  },
  update: async (id, data) => {
    const { id: _, created_at, ...rest } = data;
    await fetch(`${SUPABASE_URL}/rest/v1/partners?id=eq.${id}`, {
      method: "PATCH",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json", Prefer: "return=representation" },
      body: JSON.stringify(rest),
    });
  },
  delete: async (id) => {
    await fetch(`${SUPABASE_URL}/rest/v1/partners?id=eq.${id}`, {
      method: "DELETE",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
  },
};

const DEFAULT_TRADE_TYPES = ["Interior Designer", "Builder", "Architect", "Decorator", "Real Estate Agent", "Property Manager", "Other"];

const emptyPartner = () => ({
  company_name: "", contact_name: "", phone: "", email: "", suburb: "",
  office_address: "",
  trade_type: DEFAULT_TRADE_TYPES[0], last_contacted: "", next_contact: "",
  notes: [], referrals: [], jobs_referred: 0,
});

// ─── WIP Jobs API ─────────────────────────────────────────────────────────────
const wipApi = {
  getByLead: async (leadId) => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/wip_jobs?lead_id=eq.${leadId}&limit=1`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
    const data = await res.json();
    return data[0] || null;
  },
  getAll: async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/wip_jobs?select=*`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
    return res.json();
  },
  upsert: async (job) => {
    const method = job.id ? "PATCH" : "POST";
    const url = job.id
      ? `${SUPABASE_URL}/rest/v1/wip_jobs?id=eq.${job.id}`
      : `${SUPABASE_URL}/rest/v1/wip_jobs`;
    const { id, created_at, ...rest } = job;
    const body = { ...rest };
    console.log("WIP upsert body:", JSON.stringify(body, null, 2));
    const res = await fetch(url, {
      method,
      headers: {
        apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`,
        "Content-Type": "application/json", Prefer: "return=representation",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.text();
      console.error("WIP upsert error:", res.status, err);
      return null;
    }
    const data = await res.json();
    return Array.isArray(data) ? data[0] : data;
  },
};


// ─── Completed Jobs API ───────────────────────────────────────────────────────
const completedJobsApi = {
  insert: async (data) => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/completed_jobs`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json", Prefer: "return=representation" },
      body: JSON.stringify(data),
    });
    return res.json();
  },
  getAll: async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/completed_jobs?order=installed_at.desc`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
    return res.json();
  },
  delete: async (id) => {
    await fetch(`${SUPABASE_URL}/rest/v1/completed_jobs?id=eq.${id}`, {
      method: "DELETE",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
  },
};

const emptyWipJob = (leadId) => ({
  lead_id: leadId,
  deposit_date: "",
  installer_request: "",
  scheduled_install: "",
  order_ready: false,
  installation_booked: false,
  customer_contacted: false,
  installed_at: null,
  products: Object.fromEntries(DEFAULT_PRODUCTS.map(p => [p.key, null])),
  components: Object.fromEntries(DEFAULT_COMPONENTS.map(c => [c.key, null])),
  received: {},
  component_meta: {},
  wip_notes: [],
});

// ─── Status Date Popup ───────────────────────────────────────────────────────
const StatusDatePopup = ({ status, onConfirm, onSkip }) => {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceValue, setInvoiceValue] = useState("");
  const isApproved = status === "Approved";
  const icon = isApproved ? "✅" : "❌";
  const label = isApproved ? "Approval Date" : "Lost Date";
  const color = isApproved ? "#10B981" : "#EF4444";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 250, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 420, boxShadow: "0 24px 64px rgba(0,0,0,0.25)", padding: "32px 36px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", marginBottom: 6 }}>Status changed to {status}</div>
        <div style={{ fontSize: 14, color: "#64748B", marginBottom: 24 }}>Please fill in the details below</div>
        <div style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
          <div>
            <label style={labelStyle}>{label}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </div>
          {isApproved && <>
            <div>
              <label style={labelStyle}>Invoice Number</label>
              <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} style={inputStyle} placeholder="e.g. INV-1042" />
            </div>
            <div>
              <label style={labelStyle}>Invoice Value ($)</label>
              <input type="number" value={invoiceValue} onChange={(e) => setInvoiceValue(e.target.value)} style={inputStyle} placeholder="e.g. 4800" />
            </div>
          </>}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onSkip}
            style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>
            Skip
          </button>
          <button onClick={() => onConfirm({ date, invoiceNumber, invoiceValue: invoiceValue ? parseFloat(invoiceValue) : null })}
            style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: color, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Awesome Job Popup ───────────────────────────────────────────────────────
const AwesomeJobPopup = ({ name, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 420, boxShadow: "0 24px 64px rgba(0,0,0,0.25)", padding: "44px 40px", textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#0F172A", marginBottom: 8 }}>Awesome Job!</div>
        <div style={{ fontSize: 16, color: "#64748B", marginBottom: 6 }}>
          <strong style={{ color: "#8DC73F" }}>{name}</strong> has been approved!
        </div>
        <div style={{ fontSize: 14, color: "#94A3B8", marginBottom: 28 }}>The team is crushing it 💪</div>
        <button onClick={onClose}
          style={{ padding: "11px 32px", borderRadius: 10, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
          Let's Go!
        </button>
      </div>
    </div>
  );
};



// ─── Check Measure PDF (mirrored from measure app) ───────────────────────────
const loadJsPDFCM = () => new Promise((resolve, reject) => {
  if (window.jspdf) return resolve(window.jspdf.jsPDF);
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
  s.onload = () => resolve(window.jspdf.jsPDF);
  s.onerror = reject;
  document.head.appendChild(s);
});

// eslint-disable-next-line no-unused-vars
const loadImageCM = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const exportCMPDF = async (job) => {
  const jsPDF = await loadJsPDFCM();
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = 210, mx = 15, mxr = 195;
  const cw = mxr - mx;
  let y = 0;

  const addHeader = async (pageNum, totalPages) => {
    doc.setFillColor(28, 46, 15);
    doc.rect(0, 0, pw, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Curtain Ideas", mx, 13);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Page ${pageNum} of ${totalPages}`, mxr, 13, { align: "right" });
    doc.setTextColor(15, 23, 42);
    return 28;
  };

  const windows = Array.isArray(job.windows) ? job.windows : (job.windows?.items || []);
  let totalPages = 1 + windows.length;

  y = await addHeader(1, totalPages);

  // Cover
  doc.setFontSize(22); doc.setFont("helvetica", "bold");
  doc.text("Check Measure Report", mx, y + 8);
  y += 16;

  const address = [job.street, job.suburb, job.postcode].filter(Boolean).join(", ");
  const dateStr = job.measure_date ? new Date(job.measure_date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "";

  doc.setFillColor(241, 245, 249);
  doc.roundedRect(mx, y, cw, 38, 3, 3, "F");
  y += 8;
  doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(148, 163, 184);
  doc.text("LEAD DETAILS", mx + 6, y); y += 7;
  doc.setFontSize(11); doc.setTextColor(15, 23, 42); doc.setFont("helvetica", "bold");
  doc.text(job.lead_name || "—", mx + 6, y); y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  if (address) { doc.text(address, mx + 6, y); y += 5; }
  if (job.phone) { doc.text(`Phone: ${job.phone}`, mx + 6, y); y += 5; }
  if (dateStr) { doc.text(`Measure: ${dateStr}${job.measure_time ? " at " + job.measure_time : ""}`, mx + 6, y); }
  y += 20;

  // Window summary table
  doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(28, 46, 15);
  doc.text("Window Summary", mx, y); y += 8;
  doc.setFillColor(28, 46, 15);
  doc.rect(mx, y, cw, 7, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text("#", mx + 2, y + 5); doc.text("Window", mx + 9, y + 5);
  doc.text("In W", mx + 60, y + 5); doc.text("In L", mx + 78, y + 5);
  doc.text("Trt W", mx + 118, y + 5); doc.text("Trt D", mx + 136, y + 5);
  y += 7;
  doc.setTextColor(15, 23, 42); doc.setFont("helvetica", "normal");
  windows.forEach((w, i) => {
    const bg = i % 2 === 0 ? 248 : 241;
    doc.setFillColor(bg, bg, bg); doc.rect(mx, y, cw, 6, "F");
    doc.setFontSize(7);
    doc.text(`${i + 1}`, mx + 2, y + 4.5);
    doc.text((w.label || "—").substring(0, 22), mx + 9, y + 4.5);
    doc.text(w.measurements?.inside_width || "—", mx + 60, y + 4.5);
    doc.text(w.measurements?.inside_length || "—", mx + 78, y + 4.5);
    doc.text(w.measurements?.treatment_width || "—", mx + 118, y + 4.5);
    doc.text(w.measurements?.treatment_drop || "—", mx + 136, y + 4.5);
    y += 6;
  });

  // Window detail pages
  let currentPage = 1;
  for (let wIdx = 0; wIdx < windows.length; wIdx++) {
    const w = windows[wIdx];
    doc.addPage(); currentPage++;
    y = await addHeader(currentPage, totalPages);

    doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(28, 46, 15);
    doc.text(`Window ${wIdx + 1}: ${w.label || ""}`, mx, y + 6);

    const measFields = [
      ["Inside Width", w.measurements?.inside_width],
      ["Outside Width", w.measurements?.outside_width],
      ["Inside Length", w.measurements?.inside_length],
      ["Outside Length", w.measurements?.outside_length],
      ["Treatment Width", w.measurements?.treatment_width],
      ["Treatment Drop", w.measurements?.treatment_drop],
      ["L Wall to Arch.", w.measurements?.left_wall_to_arch],
      ["R Wall to Arch.", w.measurements?.right_wall_to_arch],
      ["Arch. to Floor", w.measurements?.arch_to_floor],
      [w.has_cornices ? "Cornice to Floor" : "Ceiling to Floor", w.measurements?.to_floor],
    ];

    let contentY = y + 14;
    const measX = mx + 76;
    doc.setFillColor(28, 46, 15);
    doc.rect(measX, contentY, mxr - measX, 6, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.setFont("helvetica", "bold");
    doc.text("Measurement", measX + 2, contentY + 4.5);
    doc.text("mm", mxr - 2, contentY + 4.5, { align: "right" });
    contentY += 6;

    doc.setTextColor(15, 23, 42); doc.setFont("helvetica", "normal");
    measFields.forEach(([label, val], mi) => {
      const bg = mi % 2 === 0 ? 248 : 241;
      doc.setFillColor(bg, bg, bg); doc.rect(measX, contentY, mxr - measX, 5, "F");
      doc.setFontSize(7); doc.text(label, measX + 2, contentY + 3.8);
      doc.setFont("helvetica", "bold"); doc.text(val || "—", mxr - 2, contentY + 3.8, { align: "right" });
      doc.setFont("helvetica", "normal"); contentY += 5;
    });

    if (w.comments) {
      const commentsY = y + 14 + Math.max(52, 6 + measFields.length * 5) + 4;
      doc.setFillColor(242, 249, 231);
      doc.roundedRect(mx, commentsY, cw, 14, 2, 2, "F");
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(141, 199, 63);
      doc.text("COMMENTS", mx + 3, commentsY + 4);
      doc.setFont("helvetica", "normal"); doc.setTextColor(15, 23, 42); doc.setFontSize(8);
      doc.text(doc.splitTextToSize(w.comments, cw - 6).slice(0, 2), mx + 3, commentsY + 9);
    }
  }

  const namePart = (job.lead_name || "measure").replace(/\s+/g, "_");
  const addressPart = [job.street, job.suburb].filter(Boolean).join("_").replace(/\s+/g, "_");
  const filename = `${namePart}${addressPart ? "_" + addressPart : ""}_CM.pdf`;
  const blob = doc.output("blob");
  return { blob, filename };
};

// ─── Book Check Measure ───────────────────────────────────────────────────────
const BookCheckMeasurePopup = ({ lead, form, teamMembers, onBook, onClose }) => {
  const [date, setDate] = useState(form.cm_date || "");
  const [time, setTime] = useState(form.cm_time || "09:00");
  const [assignee, setAssignee] = useState(teamMembers[0] || "");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);

  const generateICS = (d, t, name, address, phone, assigneeName, noteText) => {
    const dt = new Date(`${d}T${t}:00`);
    const dtEnd = new Date(dt.getTime() + 60 * 60 * 1000); // 1 hour
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const desc = [
      `Lead: ${name}`,
      `Phone: ${phone || "—"}`,
      `Address: ${address || "—"}`,
      `Assigned to: ${assigneeName}`,
      noteText ? `Notes: ${noteText}` : null,
    ].filter(Boolean).join("\n");

    return [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Curtain Ideas//Check Measure//EN",
      "BEGIN:VEVENT",
      `UID:cm-${lead.id}-${Date.now()}@curtainideas.com.au`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(dt)}`,
      `DTEND:${fmt(dtEnd)}`,
      `SUMMARY:Check Measure — ${name}`,
      `DESCRIPTION:${desc.replace(/\n/g, "\\n")}`,
      `LOCATION:${address || ""}`,
      `ORGANIZER;CN=Curtain Ideas:mailto:bookings@curtainideas.com.au`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
  };

  const handleBook = async () => {
    if (!date) return;
    setBooking(true);
    const address = [lead.street || form.street, lead.suburb || form.suburb, lead.postcode || form.postcode].filter(Boolean).join(", ");
    const name = form.name || lead.name || "";
    const phone = form.phone || lead.phone || "";

    // Download .ics
    const ics = generateICS(date, time, name, address, phone, assignee, notes);
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `check-measure-${name.replace(/\s+/g, "-").toLowerCase()}.ics`;
    a.click();

    // Create check_measures record in Supabase
    let cmRecordId = null;
    try {
      cmRecordId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await fetch(`${SUPABASE_URL}/rest/v1/check_measures`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`,
          "Content-Type": "application/json", Prefer: "return=minimal",
        },
        body: JSON.stringify({
          id: cmRecordId,
          lead_id: lead.id || null,
          lead_name: name,
          phone,
          email: form.email || lead.email || "",
          street: form.street || lead.street || "",
          suburb: form.suburb || lead.suburb || "",
          postcode: form.postcode || lead.postcode || "",
          address,
          measure_date: date,
          measure_time: time,
          windows: [],
          measurements_json: [],
        }),
      });
    } catch (e) { console.warn("Check measure record creation failed:", e); cmRecordId = null; }

    // Log activity
    try {
      await activityApi.log(lead.id, getCurrentUser(), "Check measure booked", `${date} ${time} — ${assignee}`);
    } catch (e) {}

    onBook({ cm_date: date, cm_time: time, cm_assignee: assignee, cm_notes: notes, cm_record_id: cmRecordId || "" });
    setBooking(false);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 24px 64px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "22px 28px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>📐 Book Check Measure</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0F172A" }}>{form.name || lead.name}</div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>{[form.street || lead.street, form.suburb || lead.suburb].filter(Boolean).join(", ")}</div>
          </div>
          <button onClick={onClose} style={{ background: "#F1F5F9", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 18, color: "#64748B" }}>×</button>
        </div>

        {/* Form */}
        <div style={{ padding: "20px 28px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label style={labelStyle}>Date *</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Time</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Assigned To</label>
            <select value={assignee} onChange={e => setAssignee(e.target.value)} style={inputStyle}>
              {teamMembers.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Notes for Measurer</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="e.g. Access via side gate, dogs on premises…"
              style={{ ...inputStyle, resize: "none", lineHeight: 1.5 }} />
          </div>
          {!date && <div style={{ gridColumn: "1/-1", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>Please select a date to continue</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: "0 28px 24px", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
          <button onClick={handleBook} disabled={!date || booking}
            style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: !date ? "not-allowed" : "pointer", fontSize: 14, opacity: (!date || booking) ? 0.6 : 1, display: "flex", alignItems: "center", gap: 8 }}>
            {booking ? "Booking…" : "📅 Confirm & Download .ics"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CheckMeasureCard = ({ form, onRebook, onDelete, isCompleted, onDownloadPDF, downloading }) => {
  const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
  const fmtTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":");
    const hr = parseInt(h);
    return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
  };
  return (
    <div style={{ background: isCompleted ? "#F2F9E7" : "#F8FAFC", border: `1px solid ${isCompleted ? "#D1E8A0" : "#E2E8F0"}`, borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>📐</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", marginBottom: 1 }}>{fmt(form.cm_date)}{form.cm_time ? ` · ${fmtTime(form.cm_time)}` : ""}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "#64748B" }}>{[form.cm_assignee, form.cm_notes].filter(Boolean).join(" · ")}</span>
            {isCompleted && <span style={{ fontSize: 10, fontWeight: 700, background: "#8DC73F", color: "#fff", borderRadius: 99, padding: "1px 7px", flexShrink: 0 }}>✓ Done</span>}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {isCompleted ? (
          <button onClick={onDownloadPDF} disabled={downloading}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "none", background: "#1C2E0F", color: "#8DC73F", fontWeight: 700, cursor: downloading ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: downloading ? 0.7 : 1, whiteSpace: "nowrap" }}>
            {downloading ? "…" : "📄 PDF"}
          </button>
        ) : (
          <>
            <button onClick={onRebook} style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Rebook</button>
            <button onClick={onDelete} style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #FECACA", background: "#FEF2F2", color: "#EF4444", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
          </>
        )}
      </div>
    </div>
  );
};

const Modal = ({ lead, onClose, onSave, onDelete, isNew }) => {
  const { sources: SOURCES, statuses: STATUS, warmth: WARMTH, teamMembers: TEAM_MEMBERS } = useSettings() || {
    sources: DEFAULT_SOURCES, statuses: DEFAULT_STATUS, warmth: DEFAULT_WARMTH, teamMembers: DEFAULT_TEAM_MEMBERS,
  };
  const [form, setForm] = useState({ ...lead });
  const [saving, setSaving] = useState(false);
  const [showApproved, setShowApproved] = useState(false);
  const [statusDatePopup, setStatusDatePopup] = useState(null);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [rightTab, setRightTab] = useState("notes");
  const [activityLog, setActivityLog] = useState([]);
  const [showBookCM, setShowBookCM] = useState(false);
  const [cmCompleted, setCmCompleted] = useState(false);
  const [refreshFilesKey, setRefreshFilesKey] = useState(0);
  const [cmJob, setCmJob] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const hasChanges = JSON.stringify(form) !== JSON.stringify(lead);

  useEffect(() => {
    const check = async () => {
      try {
        // Try by cm_record_id first, fall back to lead_id lookup
        const cmId = form.cm_record_id || lead.cm_record_id;
        const url = cmId
          ? `${SUPABASE_URL}/rest/v1/check_measures?id=eq.${cmId}&select=*`
          : lead.id
            ? `${SUPABASE_URL}/rest/v1/check_measures?lead_id=eq.${lead.id}&select=*&order=created_at.desc&limit=1`
            : null;
        if (!url) return;
        const res = await fetch(url, {
          headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
        });
        const data = await res.json();
        if (Array.isArray(data) && data[0]) {
          const row = data[0];
          const rawWindows = row.windows;
          const meta = (rawWindows && !Array.isArray(rawWindows)) ? (rawWindows._meta || {}) : {};
          setCmCompleted(meta.is_completed || false);
          const wins = Array.isArray(row.measurements_json) ? row.measurements_json
            : Array.isArray(rawWindows) ? rawWindows
            : (rawWindows?.items || []);
          setCmJob({ ...row, windows: wins, pdf_path: row.pdf_path || null });
          // Backfill cm_record_id on form if missing
          if (!cmId && row.id) set("cm_record_id", row.id);
        }
      } catch (e) { console.warn("CM check failed:", e); }
    };
    if (form.cm_date || lead.cm_date) check();
  }, [lead.id, form.cm_date, lead.cm_date]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadPDF = async () => {
    if (!cmJob) return;
    setDownloadingPDF(true);
    try {
      // If already saved to storage, fetch and download directly
      if (cmJob.pdf_path) {
        const url = await storage.getSignedUrl(cmJob.pdf_path);
        if (url) {
          const a = document.createElement("a");
          a.href = url;
          a.download = cmJob.pdf_path.split("/").pop();
          a.click();
          setDownloadingPDF(false);
          return;
        }
      }
      // Generate PDF
      const { blob, filename } = await exportCMPDF(cmJob);
      // Download to user
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      // Upload to Supabase Storage under lead's folder
      if (lead.id) {
        try {
          const file = new File([blob], filename, { type: "application/pdf" });
          const path = await storage.upload(lead.id, file, filename);
          // Save pdf_path back to check_measures record
          const cmId = form.cm_record_id || lead.cm_record_id;
          if (cmId) {
            await fetch(`${SUPABASE_URL}/rest/v1/check_measures?id=eq.${cmId}`, {
              method: "PATCH",
              headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json" },
              body: JSON.stringify({ pdf_path: path }),
            });
            setCmJob(j => ({ ...j, pdf_path: path }));
          setRefreshFilesKey(k => k + 1);
          }
        } catch (e) { console.warn("PDF storage upload failed:", e); }
      }
    } catch (e) { alert("PDF generation failed"); }
    setDownloadingPDF(false);
  };

  const handleClose = () => {
    if (hasChanges) setShowUnsaved(true);
    else onClose();
  };

  const handleStatusChange = (val) => {
    set("status", val);
    if (val === "Approved") { setStatusDatePopup("Approved"); setShowApproved(true); }
    else if (val === "Rejected") setStatusDatePopup("Rejected");
  };

  const handleStatusDateConfirm = ({ date, invoiceNumber, invoiceValue }) => {
    if (statusDatePopup === "Approved") {
      set("approved_date", date);
      if (invoiceNumber) set("invoice_number", invoiceNumber);
      if (invoiceValue) set("invoice_value", invoiceValue);
    }
    if (statusDatePopup === "Rejected") set("rejected_date", date);
    setStatusDatePopup(null);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form, lead);
    // Auto-create WIP job if newly approved and no WIP job exists yet
    if (form.status === "Approved" && lead.status !== "Approved" && form.id) {
      const existing = await wipApi.getByLead(form.id);
      if (!existing) {
        await wipApi.upsert({
          ...emptyWipJob(form.id),
          deposit_date: form.approved_date || null,
        });
      }
    }
    setSaving(false);
    onClose();
  };

  useEffect(() => {
    if (!isNew && lead.id) {
      activityApi.getForLead(lead.id).then(data => setActivityLog(Array.isArray(data) ? data : []));
    }
  }, [lead.id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedNotes = [...(form.notes || [])].sort((a, b) => new Date(a.date) - new Date(b.date));

  const statusPopups = <>
    {statusDatePopup && <StatusDatePopup status={statusDatePopup} onConfirm={handleStatusDateConfirm} onSkip={() => setStatusDatePopup(null)} />}
    {showApproved && <AwesomeJobPopup name={form.name} onClose={() => setShowApproved(false)} />}
    {showUnsaved && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: "#fff", borderRadius: 16, maxWidth: 380, width: "100%", padding: "32px 36px", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>Unsaved Changes</div>
          <div style={{ fontSize: 14, color: "#64748B", marginBottom: 24 }}>You have unsaved changes. What would you like to do?</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { setShowUnsaved(false); onClose(); }}
              style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>
              Discard
            </button>
            <button onClick={async () => { setShowUnsaved(false); await handleSave(); }}
              style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
              Save & Close
            </button>
          </div>
        </div>
      </div>
    )}
  </>;

  // For new leads, use compact single-column layout
  if (isNew) return (
    <>{statusPopups}
    {showBookCM && (
      <BookCheckMeasurePopup
        lead={lead}
        form={form}
        teamMembers={TEAM_MEMBERS}
        onClose={() => setShowBookCM(false)}
        onBook={(booking) => {
          set("cm_date", booking.cm_date);
          set("cm_time", booking.cm_time);
          set("cm_assignee", booking.cm_assignee);
          set("cm_notes", booking.cm_notes);
          set("cm_record_id", booking.cm_record_id || "");
        }}
      />
    )}
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={handleClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.18)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "28px 32px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#94A3B8", textTransform: "uppercase", marginBottom: 6 }}>New Lead</div>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Smith Residence"
              style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", border: "none", outline: "none", width: "100%", fontFamily: "inherit", background: "transparent" }} />
          </div>
          <button onClick={onClose} style={{ background: "#F1F5F9", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 18, color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div className="ci-new-modal" style={{ padding: "24px 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ gridColumn: "1/-1", fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase" }}>Contact Details</div>
          <div><label style={labelStyle}>Phone</label><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} style={inputStyle} /></div>
          <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Email</label><input value={form.email || ""} onChange={(e) => set("email", e.target.value)} style={inputStyle} /></div>
          <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Street Address</label><input value={form.street || ""} onChange={(e) => set("street", e.target.value)} style={inputStyle} placeholder="e.g. 42 Example Street" /></div>
          <div><label style={labelStyle}>Suburb</label><input value={form.suburb || ""} onChange={(e) => set("suburb", e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>Postcode</label><input value={form.postcode || ""} onChange={(e) => set("postcode", e.target.value)} style={inputStyle} /></div>
          <div style={{ gridColumn: "1/-1", borderTop: "1px solid #F1F5F9", margin: "4px 0" }} />
          <div style={{ gridColumn: "1/-1", fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase" }}>Quote Details</div>
          <div><label style={labelStyle}>Status</label><select value={form.status} onChange={(e) => handleStatusChange(e.target.value)} style={inputStyle}>{STATUS.map(s => <option key={s}>{s}</option>)}</select></div>
          <div><label style={labelStyle}>Source</label><select value={form.source} onChange={(e) => set("source", e.target.value)} style={inputStyle}>{SOURCES.map(s => <option key={s}>{s}</option>)}</select></div>
          <div><label style={labelStyle}>Warmth</label><select value={form.warmth} onChange={(e) => set("warmth", e.target.value)} style={inputStyle}>{WARMTH.map(s => <option key={s}>{s}</option>)}</select></div>
          <div><label style={labelStyle}>Quote Value ($)</label><input type="number" value={form.quote_value ?? ""} onChange={(e) => set("quote_value", e.target.value)} style={inputStyle} placeholder="Not yet quoted" /></div>
          <div><label style={labelStyle}>Quote Number</label><input value={form.quote_number || ""} onChange={(e) => set("quote_number", e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>Book Number</label><input value={form.book_number || ""} onChange={(e) => set("book_number", e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>First Contact Date</label><input type="date" value={form.first_contact || ""} onChange={(e) => set("first_contact", e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>Next Contact Date</label><input type="date" value={form.next_contact || ""} onChange={(e) => set("next_contact", e.target.value)} style={inputStyle} /></div>
          <div style={{ gridColumn: "1/-1" }}>
            {form.cm_date ? (
              <CheckMeasureCard form={form} isCompleted={cmCompleted} onDownloadPDF={handleDownloadPDF} downloading={downloadingPDF} onRebook={() => setShowBookCM(true)} onDelete={async () => {
          if (window.confirm("Remove this check measure booking?")) {
            if (form.cm_record_id) {
              try {
                await fetch(`${SUPABASE_URL}/rest/v1/check_measures?id=eq.${form.cm_record_id}`, {
                  method: "DELETE",
                  headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
                });
              } catch (e) { console.warn("Failed to delete check measure record:", e); }
            }
            set("cm_date", ""); set("cm_time", ""); set("cm_assignee", ""); set("cm_notes", ""); set("cm_record_id", "");
          }
        }} />
            ) : (
              <button onClick={() => setShowBookCM(true)}
                style={{ width: "100%", padding: "10px 16px", borderRadius: 8, border: "2px dashed #8DC73F", background: "#F2F9E7", color: "#3B6D11", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                📐 Book Check Measure
              </button>
            )}
          </div>
          <div><label style={labelStyle}>Quote Sent Date</label><input type="date" value={form.quote_sent_date || ""} onChange={(e) => set("quote_sent_date", e.target.value)} style={inputStyle} /></div>
          <div style={{ gridColumn: "1/-1", borderTop: "1px solid #F1F5F9", margin: "4px 0" }} />
          <div style={{ gridColumn: "1/-1", fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase" }}>Notes</div>
          <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Brief Note (shown in table)</label><input value={form.brief_note || ""} onChange={(e) => set("brief_note", e.target.value)} style={inputStyle} /></div>
          <NotesEditor notes={form.notes || []} onChange={(notes) => set("notes", notes)} />
        </div>
        <div style={{ padding: "16px 32px 28px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={handleClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: 14, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving..." : "Add Lead"}
          </button>
        </div>
      </div>
    </div></>
  );

  // Existing lead — two-column layout
  return (
    <>{statusPopups}
    {showBookCM && (
      <BookCheckMeasurePopup
        lead={lead}
        form={form}
        teamMembers={TEAM_MEMBERS}
        onClose={() => setShowBookCM(false)}
        onBook={(booking) => {
          set("cm_date", booking.cm_date);
          set("cm_time", booking.cm_time);
          set("cm_assignee", booking.cm_assignee);
          set("cm_notes", booking.cm_notes);
          set("cm_record_id", booking.cm_record_id || "");
        }}
      />
    )}
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={handleClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 1100, height: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.22)" }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "22px 28px 18px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#94A3B8", textTransform: "uppercase", marginBottom: 5 }}>Lead Details</div>
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
              style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", border: "none", outline: "none", width: "100%", fontFamily: "inherit", background: "transparent" }} />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={() => { if (window.confirm("Delete this lead?")) { onDelete(lead.id); onClose(); } }}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #FEE2E2", background: "#FEF2F2", color: "#DC2626", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
              Delete
            </button>
            <button onClick={handleClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: 13, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={handleClose} style={{ background: "#F1F5F9", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 18, color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
        </div>

        {/* Body — two columns */}
        <div className="ci-modal-two-col" style={{ display: "grid", gridTemplateColumns: "380px 1fr", flex: 1, overflow: "hidden" }}>

          {/* LEFT — details + files */}
          <div style={{ overflowY: "auto", borderRight: "1px solid #F1F5F9", padding: "24px 24px" }}>

            {/* Contact */}
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase", marginBottom: 12 }}>Contact Details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 4 }}>
              <div><label style={labelStyle}>Phone</label>
                <div style={{ position: "relative" }}>
                  <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} style={inputStyle} />
                  {form.phone && <a href={"tel:" + form.phone} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, textDecoration: "none" }}>📞</a>}
                </div>
              </div>
              <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Email</label>
                <div style={{ position: "relative" }}>
                  <input value={form.email || ""} onChange={(e) => set("email", e.target.value)} style={inputStyle} />
                  {form.email && <a href={"mailto:" + form.email} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, textDecoration: "none" }}>✉️</a>}
                </div>
              </div>
              <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Street</label><input value={form.street || ""} onChange={(e) => set("street", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Suburb</label><input value={form.suburb || ""} onChange={(e) => set("suburb", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Postcode</label><input value={form.postcode || ""} onChange={(e) => set("postcode", e.target.value)} style={inputStyle} /></div>
            </div>

            {/* Quote Details */}
            <div style={{ borderTop: "1px solid #F1F5F9", margin: "16px 0 12px" }} />
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase", marginBottom: 12 }}>Quote Details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 4 }}>
              <div><label style={labelStyle}>Status</label><select value={form.status} onChange={(e) => handleStatusChange(e.target.value)} style={inputStyle}>{STATUS.map(s => <option key={s}>{s}</option>)}</select></div>
              <div><label style={labelStyle}>Warmth</label><select value={form.warmth} onChange={(e) => set("warmth", e.target.value)} style={inputStyle}>{WARMTH.map(s => <option key={s}>{s}</option>)}</select></div>
              <div><label style={labelStyle}>Source</label><select value={form.source} onChange={(e) => set("source", e.target.value)} style={inputStyle}>{SOURCES.map(s => <option key={s}>{s}</option>)}</select></div>
              <div><label style={labelStyle}>Quote Value ($)</label><input type="number" value={form.quote_value ?? ""} onChange={(e) => set("quote_value", e.target.value)} style={inputStyle} placeholder="Not yet quoted" /></div>
              <div><label style={labelStyle}>Quote Number</label><input value={form.quote_number || ""} onChange={(e) => set("quote_number", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Book Number</label><input value={form.book_number || ""} onChange={(e) => set("book_number", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>First Contact</label><input type="date" value={form.first_contact || ""} onChange={(e) => set("first_contact", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Next Contact</label><input type="date" value={form.next_contact || ""} onChange={(e) => set("next_contact", e.target.value)} style={inputStyle} /></div>
              <div style={{ gridColumn: "1/-1" }}>
                {form.cm_date ? (
                  <CheckMeasureCard form={form} isCompleted={cmCompleted} onDownloadPDF={handleDownloadPDF} downloading={downloadingPDF} onRebook={() => setShowBookCM(true)} onDelete={async () => {
          if (window.confirm("Remove this check measure booking?")) {
            if (form.cm_record_id) {
              try {
                await fetch(`${SUPABASE_URL}/rest/v1/check_measures?id=eq.${form.cm_record_id}`, {
                  method: "DELETE",
                  headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
                });
              } catch (e) { console.warn("Failed to delete check measure record:", e); }
            }
            set("cm_date", ""); set("cm_time", ""); set("cm_assignee", ""); set("cm_notes", ""); set("cm_record_id", "");
          }
        }} />
                ) : (
                  <button onClick={() => setShowBookCM(true)}
                    style={{ width: "100%", padding: "10px 16px", borderRadius: 8, border: "2px dashed #8DC73F", background: "#F2F9E7", color: "#3B6D11", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    📐 Book Check Measure
                  </button>
                )}
              </div>
              <div><label style={labelStyle}>Quote Sent</label><input type="date" value={form.quote_sent_date || ""} onChange={(e) => set("quote_sent_date", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Approved Date</label>
                <div style={{ ...inputStyle, background: "#F8FAFC", color: form.approved_date ? "#0F172A" : "#CBD5E1", cursor: "default" }}>
                  {form.approved_date ? new Date(form.approved_date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "Set when approved"}
                </div>
              </div>
              <div><label style={labelStyle}>Invoice Number</label>
                <div style={{ ...inputStyle, background: "#F8FAFC", color: form.invoice_number ? "#0F172A" : "#CBD5E1", cursor: "default" }}>
                  {form.invoice_number || "Set when approved"}
                </div>
              </div>
              <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Invoice Value ($)</label>
                <div style={{ ...inputStyle, background: "#F8FAFC", color: form.invoice_value ? "#10B981" : "#CBD5E1", fontWeight: form.invoice_value ? 700 : 400, cursor: "default" }}>
                  {form.invoice_value ? formatCurrency(form.invoice_value) : "Set when approved"}
                </div>
              </div>
              <div><label style={labelStyle}>Lost Date</label>
                <div style={{ ...inputStyle, background: "#F8FAFC", color: form.rejected_date ? "#0F172A" : "#CBD5E1", cursor: "default" }}>
                  {form.rejected_date ? new Date(form.rejected_date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "Set when rejected"}
                </div>
              </div>
              <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Brief Note (shown in table)</label><input value={form.brief_note || ""} onChange={(e) => set("brief_note", e.target.value)} style={inputStyle} /></div>
            </div>

            {/* Files */}
            <div style={{ borderTop: "1px solid #F1F5F9", margin: "16px 0 12px" }} />
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase", marginBottom: 12 }}>Files</div>
            <FileUploader leadId={form.id} refreshTrigger={refreshFilesKey} />
          </div>

          {/* RIGHT — notes + activity */}
          <div className="ci-modal-right" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Tab switcher */}
            <div style={{ display: "flex", borderBottom: "1px solid #F1F5F9", flexShrink: 0 }}>
              {[["notes", `Notes${sortedNotes.length > 0 ? ` · ${sortedNotes.length}` : ""}`],["activity", `Activity${activityLog.length > 0 ? ` · ${activityLog.length}` : ""}`]].map(([tab, label]) => (
                <button key={tab} onClick={() => setRightTab(tab)}
                  style={{ flex: 1, padding: "14px 0", background: "none", border: "none", borderBottom: rightTab === tab ? "2px solid #8DC73F" : "2px solid transparent", cursor: "pointer", fontSize: 11, fontWeight: 700, letterSpacing: 1.1, color: rightTab === tab ? "#8DC73F" : "#94A3B8", textTransform: "uppercase", marginBottom: -1 }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Notes feed */}
            {rightTab === "notes" && <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
              {sortedNotes.length === 0 && (
                <div style={{ textAlign: "center", color: "#CBD5E1", fontSize: 14, marginTop: 40 }}>No notes yet</div>
              )}
              {sortedNotes.map((note) => (
                <div key={note.id} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: "12px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      {note.author && <span style={{ fontSize: 12, fontWeight: 700, color: "#8DC73F" }}>{note.author}</span>}
                      <span style={{ fontSize: 12, color: "#94A3B8" }}>
                        {note.date ? new Date(note.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </span>
                    </div>
                    <button onClick={() => set("notes", (form.notes || []).filter(n => n.id !== note.id))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#CBD5E1", fontSize: 15, padding: 0, lineHeight: 1 }}>×</button>
                  </div>
                  <div style={{ fontSize: 14, color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{note.text}</div>
                </div>
              ))}
            </div>}

            {/* Activity feed */}
            {rightTab === "activity" && <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
              {activityLog.length === 0 ? (
                <div style={{ textAlign: "center", color: "#CBD5E1", fontSize: 14, marginTop: 40 }}>No activity yet</div>
              ) : activityLog.map((entry) => {
                const EVENT_ICONS = { "Lead created": "✨", "Lead updated": "✏️", "Status changed": "🔄", "Note added": "📝", "File uploaded": "📎", "WIP job updated": "🔧", "Lead deleted": "🗑️" };
                const icon = EVENT_ICONS[entry.event] || "•";
                const ts = new Date(entry.created_at);
                const timeStr = ts.toLocaleDateString("en-AU", { day: "numeric", month: "short" }) + " " + ts.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={entry.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #F8FAFC" }}>
                    <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{entry.event}</span>
                        <span style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap" }}>{timeStr}</span>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                        {entry.user_name && <span style={{ fontSize: 11, fontWeight: 700, color: "#8DC73F" }}>{entry.user_name}</span>}
                        {entry.detail && <span style={{ fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.detail}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>}

{/* Add note — pinned to bottom, only on notes tab */}
            {rightTab === "notes" && <div style={{ padding: "14px 24px 18px", borderTop: "1px solid #F1F5F9", flexShrink: 0, background: "#fff" }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input type="date" value={new Date().toISOString().split("T")[0]}
                  id="modal-note-date"
                  style={{ ...inputStyle, width: 145, flexShrink: 0 }} />
                <select id="modal-note-author" style={{ ...inputStyle, width: 110, flexShrink: 0 }}>
                  {TEAM_MEMBERS.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <textarea id="modal-note-text" placeholder="Write a note… (Cmd+Enter to add)"
                  rows={3} style={{ ...inputStyle, flex: 1, resize: "none", lineHeight: 1.5 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.metaKey) {
                      const text = e.target.value.trim();
                      const date = document.getElementById("modal-note-date").value;
                      const author = document.getElementById("modal-note-author").value;
                      if (!text) return;
                      set("notes", [{ id: Date.now(), date, author, text }, ...(form.notes || [])]);
                      e.target.value = "";
                    }
                  }} />
                <button onClick={() => {
                    const text = document.getElementById("modal-note-text").value.trim();
                    const date = document.getElementById("modal-note-date").value;
                    const author = document.getElementById("modal-note-author").value;
                    if (!text) return;
                    set("notes", [{ id: Date.now(), date, author, text }, ...(form.notes || [])]);
                    if (form.id) activityApi.log(form.id, getCurrentUser(), "Note added", text.substring(0, 80)).catch(() => {});
                    document.getElementById("modal-note-text").value = "";
                  }}
                  style={{ padding: "10px 16px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, alignSelf: "flex-end", height: 42 }}>
                  + Add
                </button>
              </div>
            </div>}
          </div>
        </div>
      </div>
    </div></>
  );
};

const QuickEditModal = ({ lead, onClose, onSave, onViewLead }) => {
  const [form, setForm] = useState({ ...lead });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    await onSave(form, lead);
    // Auto-create WIP job if newly approved and no WIP job exists yet
    if (form.status === "Approved" && lead.status !== "Approved" && form.id) {
      const existing = await wipApi.getByLead(form.id);
      if (!existing) {
        await wipApi.upsert({
          ...emptyWipJob(form.id),
          deposit_date: form.approved_date || null,
        });
      }
    }
    setSaving(false);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div className="ci-quick-modal" style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,0.18)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "24px 28px 18px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>Quick Update</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>{lead.name}</div>
            <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
              {lead.phone && <a href={"tel:" + lead.phone} style={{ fontSize: 13, color: "#8DC73F", fontWeight: 600, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>📞 {lead.phone}</a>}
              {lead.email && <a href={"mailto:" + lead.email} style={{ fontSize: 13, color: "#8DC73F", fontWeight: 600, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>✉️ {lead.email}</a>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "#F1F5F9", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 18, color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ padding: "20px 28px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <label style={labelStyle}>Next Contact Date</label>
            <input type="date" value={form.next_contact || ""} onChange={(e) => set("next_contact", e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Add Note</label>
            <NotesEditor notes={form.notes || []} onChange={(notes) => set("notes", notes)} compact />
          </div>
        </div>
        <div style={{ padding: "12px 28px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => { onClose(); onViewLead(lead); }}
            style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#F8FAFC", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><polyline points="15 3 21 3 21 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            View Full Lead
          </button>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: 14, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const URGENCY_STYLES = {
  overdue: { bg: "#FEF2F2", border: "#FECACA", label: "Overdue",  labelBg: "#FEE2E2", labelColor: "#DC2626" },
  today:   { bg: "#FFFBEB", border: "#FDE68A", label: "Due Today", labelBg: "#FEF3C7", labelColor: "#B45309" },
  upcoming:{ bg: "#fff",    border: "#E2E8F0", label: "Upcoming",  labelBg: "#F1F5F9", labelColor: "#475569" },
};

const ContactRequired = ({ leads, onSave, onViewLead }) => {
  const [quickEdit, setQuickEdit] = useState(null);

  const contactLeads = useMemo(() => {
    return leads
      .filter(l => ["In Progress", "Delayed", "Uncontactable"].includes(l.status) && l.next_contact)
      .map(l => ({ ...l, urgency: getContactUrgency(l.next_contact) }))
      .filter(l => l.urgency !== null)
      .sort((a, b) => {
        const order = { overdue: 0, today: 1, upcoming: 2 };
        if (order[a.urgency] !== order[b.urgency]) return order[a.urgency] - order[b.urgency];
        return new Date(a.next_contact) - new Date(b.next_contact);
      });
  }, [leads]);

  if (contactLeads.length === 0) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.2, textTransform: "uppercase" }}>Leads to Contact</div>
        <span style={{ background: "#DC262622", color: "#DC2626", borderRadius: 99, fontSize: 11, fontWeight: 700, padding: "2px 8px" }}>{contactLeads.length}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {contactLeads.map((lead) => {
          const s = URGENCY_STYLES[lead.urgency];
          return (
            <div key={lead.id} onClick={() => setQuickEdit(lead)}
              style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12, padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, transition: "opacity 0.15s" }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
                <span style={{ background: s.labelBg, color: s.labelColor, fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 99, whiteSpace: "nowrap" }}>{s.label}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{lead.name}</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 1 }}>{lead.phone || ""}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>Next contact</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: lead.urgency === "overdue" ? "#DC2626" : "#0F172A" }}>{formatDate(lead.next_contact)}</div>
                </div>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" style={{ color: "#94A3B8", flexShrink: 0 }}><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </div>
          );
        })}
      </div>
      {quickEdit && (
        <QuickEditModal lead={quickEdit} onClose={() => setQuickEdit(null)} onSave={onSave} onViewLead={onViewLead} />
      )}
    </div>
  );
};

const DashboardPage = ({ leads, onSaveLead, onViewLead }) => {
  const mtdLeads = leads.filter(l => isMTD(l.first_contact));

  // Stats
  const pipelineValue = leads.filter(l => l.status === "In Progress" && l.quote_value).reduce((s, l) => s + Number(l.quote_value), 0);
  const wonMTD = leads.filter(l => l.status === "Approved" && l.quote_value && isMTD(l.approved_date)).reduce((s, l) => s + Number(l.quote_value), 0);
  const leadsThisMonth = mtdLeads.length;
  const activeLeads = leads.filter(l => !["Approved", "Rejected"].includes(l.status)).length;

  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const currentMonth = `${monthNames[thisMonth]} ${thisYear}`;

  // Warmth colours
  const WARMTH_BAR_COLORS = { "Unlikely": "#EF4444", "Likely": "#F59E0B", "Verbally Approved": "#8DC73F" };

  // Chart data — 3 bars: In Progress by warmth (stacked), Won MTD, Lost MTD
  const inProgressLeads = leads.filter(l => l.status === "In Progress" && l.quote_value);
  const warmthChartData = [{
    name: "In Progress",
    "Unlikely":          inProgressLeads.filter(l => l.warmth === "Unlikely").reduce((s, l) => s + Number(l.quote_value), 0),
    "Likely":            inProgressLeads.filter(l => l.warmth === "Likely").reduce((s, l) => s + Number(l.quote_value), 0),
    "Verbally Approved": inProgressLeads.filter(l => l.warmth === "Verbally Approved").reduce((s, l) => s + Number(l.quote_value), 0),
  }, {
    name: `Won ${currentMonth}`,
    "Approved": leads.filter(l => l.status === "Approved" && l.quote_value && isMTD(l.approved_date)).reduce((s, l) => s + Number(l.quote_value), 0),
  }, {
    name: `Lost ${currentMonth}`,
    "Rejected": leads.filter(l => l.status === "Rejected" && l.quote_value && isMTD(l.rejected_date)).reduce((s, l) => s + Number(l.quote_value), 0),
  }];

  const chartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, padding: "10px 16px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
        <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 6 }}>{label}</div>
        {payload.map(p => p.value > 0 && (
          <div key={p.dataKey} style={{ fontSize: 13, color: p.fill, fontWeight: 600 }}>{p.dataKey}: {formatCurrency(p.value)}</div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* Stat cards */}
      <div className="ci-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 28 }}>
        <StatCard label="Total Leads" value={leads.length} color="#8DC73F" />
        <StatCard label="Active Leads" value={activeLeads} color="#8B5CF6" />
        <StatCard label="New Leads This Month" value={leadsThisMonth} color="#3B82F6" sub={currentMonth} />
        <StatCard label="Pipeline Value" value={formatCurrency(pipelineValue)} color="#F59E0B" sub="In Progress only" />
        <StatCard label="Won Value" value={formatCurrency(wonMTD)} color="#10B981" sub={currentMonth} />
      </div>

      {/* Chart */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "24px 28px", border: "1px solid #E2E8F0" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 20 }}>Quote Value Overview</div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={warmthChartData} margin={{ top: 0, right: 0, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#64748B", fontWeight: 600 }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <Tooltip content={chartTooltip} cursor={{ fill: "#F8FAFC" }} />
            <Legend wrapperStyle={{ fontSize: 13, paddingTop: 12 }} />
            <Bar dataKey="Unlikely" stackId="a" fill={WARMTH_BAR_COLORS["Unlikely"]} radius={[0,0,0,0]} />
            <Bar dataKey="Likely" stackId="a" fill={WARMTH_BAR_COLORS["Likely"]} radius={[0,0,0,0]} />
            <Bar dataKey="Verbally Approved" stackId="a" fill={WARMTH_BAR_COLORS["Verbally Approved"]} radius={[6,6,0,0]} />
            <Bar dataKey="Approved" fill="#10B981" radius={[6,6,0,0]} />
            <Bar dataKey="Rejected" fill="#EF4444" radius={[6,6,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const LeadsPage = ({ leads, onSelectLead, onAddLead, onImport }) => {
  const { statuses: STATUS } = useSettings() || { statuses: DEFAULT_STATUS };
  const [filterStage, setFilterStage] = useState("In Progress");
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sortCol, setSortCol] = useState("quote_sent_date");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setImporting(true);
    setImportResult(null);
    try {
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const parseDate = (v) => {
        if (!v || v === "") return null;
        if (typeof v === "number") {
          const d = XLSX.SSF.parse_date_code(v);
          if (d) return `${d.y}-${String(d.m).padStart(2,"0")}-${String(d.d).padStart(2,"0")}`;
        }
        try { const d = new Date(v); if (!isNaN(d)) return d.toISOString().split("T")[0]; } catch {}
        return null;
      };

      const parseNoteDate = (text) => {
        if (!text || typeof text !== "string") return { date: null, clean: text };
        const m = text.trim().match(/^(\d{1,2})[/-](\d{1,2})([/-]\d{2,4})?\s*/);
        if (m) {
          const day = m[1].padStart(2,"0"), mon = m[2].padStart(2,"0");
          const yearMatch = m[3] ? m[3].replace(/[/-]/,"") : "2026";
          const y = yearMatch.length === 2 ? "20" + yearMatch : yearMatch;
          return { date: `${y}-${mon}-${day}`, clean: text.slice(m[0].length).trim() };
        }
        return { date: null, clean: text.trim() };
      };

      const splitQuoteBook = (val) => {
        if (!val && val !== 0) return ["", ""];
        const s = String(val).trim();
        if (s.includes("/")) { const p = s.split("/", 2); return [p[0].trim(), p[1].trim()]; }
        return [s, ""];
      };

      const noteKeys = Object.keys(rows[0] || {}).filter(k => /^note/i.test(k));

      const leads = rows.map((row, idx) => {
        const [qNum, bNum] = splitQuoteBook(row["Quote Number/Book No"] || row["Quote Number"] || "");
        const notes = noteKeys.map((k, ni) => {
          const txt = String(row[k] || "").trim();
          if (!txt) return null;
          const { date, clean } = parseNoteDate(txt);
          return clean ? { id: Date.now() + idx * 100 + ni, date: date || parseDate(row["First Contact Date"]) || "", text: clean } : null;
        }).filter(Boolean);

        return {
          name: String(row["Name"] || "").trim() || null,
          phone: String(row["Phone"] || "").trim() || null,
          email: String(row["Email"] || "").trim() || null,
          postcode: row["Postcode"] ? String(parseInt(row["Postcode"])) : null,
          source: String(row["Source"] || "").trim() || null,
          first_contact: parseDate(row["First Contact Date"]),
          cm_date: parseDate(row["Check Measure Date"]),
          quote_sent_date: parseDate(row["Quote Date"]),
          quote_number: qNum || null,
          book_number: bNum || null,
          quote_value: row["Estimated Quote Value"] ? parseFloat(row["Estimated Quote Value"]) : null,
          status: String(row["Status"] || "").trim() || null,
          warmth: String(row["Warmth"] || "").trim() || null,
          next_contact: parseDate(row["Next Contact Date"]),
          stage: String(row["Stage"] || "").trim() || null,
          notes: JSON.stringify(notes),
          brief_note: null, detailed_note: null, street: null, suburb: null,
        };
      }).filter(l => l.name);

      await onImport(leads);
      setImportResult({ success: true, count: leads.length });
    } catch (err) {
      console.error(err);
      setImportResult({ success: false, error: err.message });
    }
    setImporting(false);
  };

  const filtered = useMemo(() => {
    const arr = leads.filter((l) => {
      const matchStatus = l.status === filterStage;
      const matchSearch = [l.name, l.brief_note].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()));
      return matchStatus && matchSearch;
    });
    arr.sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [leads, filterStage, search, sortCol, sortDir]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}>Leads</div>
          <div style={{ fontSize: 14, color: "#64748B", marginTop: 2 }}>Manage quotes and track customer pipeline</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads…"
            style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 14, background: "#fff", outline: "none", minWidth: 220, color: "#0F172A" }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {STATUS.map((s, i) => (<React.Fragment key={s}>
              {i > 0 && STATUS[i - 1] === "In Progress" && (
                <div style={{ width: 1, height: 22, background: "#E2E8F0", margin: "0 2px" }} />
              )}
              <button onClick={() => setFilterStage(s)}
                style={{ padding: "7px 14px", borderRadius: 20, border: "1px solid", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  borderColor: filterStage === s ? "#8DC73F" : "#E2E8F0",
                  background: filterStage === s ? "#F2F9E7" : "#fff",
                  color: filterStage === s ? "#8DC73F" : "#64748B" }}>
                {s}
              </button>
            </React.Fragment>))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {importResult && (
            <span style={{ fontSize: 13, color: importResult.success ? "#8DC73F" : "#EF4444", fontWeight: 600 }}>
              {importResult.success ? `✓ ${importResult.count} leads imported` : `✗ ${importResult.error}`}
            </span>
          )}
          <label style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #E2E8F0", borderRadius: 8, padding: "9px 16px", fontWeight: 600, fontSize: 14, cursor: importing ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, opacity: importing ? 0.6 : 1 }}>
            📥 {importing ? "Importing…" : "Import Excel"}
            <input type="file" accept=".xlsx,.xls" onChange={handleImport} style={{ display: "none" }} disabled={importing} />
          </label>
          <button onClick={onAddLead}
            style={{ background: "#8DC73F", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Lead
          </button>
        </div>
      </div>
      <div className="ci-leads-table-wrap" style={{ background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0" }}>
        <table className="ci-leads-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F8FAFC" }}>
              {[
                { label: "Project", col: "name" },
                { label: "Status", col: "status" },
                { label: "Source", col: "source" },
                { label: "Warmth", col: "warmth" },
                { label: "First Contact", col: "first_contact" },
                { label: "Next Contact", col: "next_contact" },
                { label: "Quote Value", col: "quote_value" },
                { label: "Quote Date", col: "quote_sent_date" },
                { label: "Note", col: "brief_note" },
              ].map(({ label, col }) => (
                <th key={col} onClick={() => handleSort(col)}
                  style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: sortCol === col ? "#8DC73F" : "#94A3B8", letterSpacing: 1.1, textTransform: "uppercase", borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}>
                  {label} {sortCol === col ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ opacity: 0.3 }}>↕</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 15 }}>No leads found.</td></tr>
            ) : filtered.map((lead, i) => (
              <tr key={lead.id} onClick={() => onSelectLead(lead)}
                style={{ borderBottom: i < filtered.length - 1 ? "1px solid #F1F5F9" : "none", cursor: "pointer" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "13px 16px", fontWeight: 700, fontSize: 14, color: "#0F172A", whiteSpace: "nowrap" }}>{lead.name}</td>
                <td style={{ padding: "13px 16px" }}>
                  {lead.status ? <span style={{ fontSize: 11, background: "#F1F5F9", color: "#475569", borderRadius: 5, padding: "2px 8px", fontWeight: 600, whiteSpace: "nowrap" }}>{lead.status}</span> : <span style={{ color: "#CBD5E1" }}>—</span>}
                </td>
                <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B" }}>{lead.source}</td>
                <td style={{ padding: "13px 16px" }}><WarmthBadge warmth={lead.warmth} /></td>
                <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B", whiteSpace: "nowrap" }}>{formatDate(lead.first_contact)}</td>
                <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                  {lead.next_contact ? (() => {
                    const urgency = getContactUrgency(lead.next_contact);
                    return <span style={{ fontSize: 13, fontWeight: urgency ? 700 : 400, color: urgency === "overdue" ? "#DC2626" : urgency === "today" ? "#D97706" : urgency === "upcoming" ? "#F59E0B" : "#64748B" }}>{formatDate(lead.next_contact)}</span>;
                  })() : <span style={{ color: "#CBD5E1", fontSize: 13 }}>—</span>}
                </td>
                <td style={{ padding: "13px 16px", fontSize: 14, fontWeight: 600, color: lead.quote_value ? "#0F172A" : "#CBD5E1" }}>{formatCurrency(lead.quote_value)}</td>
                <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B", whiteSpace: "nowrap" }}>{formatDate(lead.quote_sent_date)}</td>
                <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B", maxWidth: 200 }}>
                  <span style={{ display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{lead.brief_note}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 12, fontSize: 13, color: "#94A3B8" }}>Showing {filtered.length} of {leads.length} leads</div>
    </div>
  );
};

// ─── WIP Job Modal ───────────────────────────────────────────────────────────
const WIPModal = ({ lead, onClose }) => {
  const { products: PRODUCTS, components: COMPONENTS } = useSettings() || {
    products: DEFAULT_PRODUCTS, components: DEFAULT_COMPONENTS,
  };
  const [job, setJob] = useState(null);
  const [originalJob, setOriginalJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [showInstalled, setShowInstalled] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(lead.invoice_number || "");
  const [invoiceValue, setInvoiceValue] = useState(lead.invoice_value || "");

  useEffect(() => {
    wipApi.getByLead(lead.id).then((data) => {
      const j = data || { ...emptyWipJob(lead.id), deposit_date: lead.approved_date || "" };
      setJob(j);
      setOriginalJob(JSON.stringify(j));
      setLoading(false);
    });
  }, [lead.id, lead.approved_date]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setJob((j) => ({ ...j, [k]: v }));
  const setProduct = (k, v) => setJob((j) => ({ ...j, products: { ...j.products, [k]: v } }));

  // Normalise a component key's value to array of line items
  const getLines = (j, k) => {
    const val = (j.components || {})[k];
    if (!val) return [];
    if (Array.isArray(val)) return val;
    // Legacy: single date string + meta + received
    const meta = (j.component_meta || {})[k] || {};
    const recv = (j.received || {})[k];
    return [{ id: "legacy", orderDate: val, supplier: meta.supplier || "", item: meta.item || "", received: recv || null }];
  };

  const updateLine = (k, lineId, field, val) => setJob((j) => {
    const lines = getLines(j, k).map(l => l.id === lineId ? { ...l, [field]: val } : l);
    return { ...j, components: { ...j.components, [k]: lines } };
  });

  const addLine = (k) => setJob((j) => {
    const lines = getLines(j, k);
    return { ...j, components: { ...j.components, [k]: [...lines, { id: Date.now().toString(), orderDate: "", supplier: "", item: "", received: null }] } };
  });

  const removeLine = (k, lineId) => setJob((j) => {
    const lines = getLines(j, k).filter(l => l.id !== lineId);
    return { ...j, components: { ...j.components, [k]: lines.length ? lines : null } };
  });

  const markLineReceived = (k, lineId) => {
    activityApi.log(lead.id, getCurrentUser(), "Component received", k).catch(() => {});
    setJob((j) => {
      const lines = getLines(j, k).map(l => l.id === lineId ? { ...l, received: { date: new Date().toISOString().split("T")[0], user: getCurrentUser() } } : l);
      return { ...j, components: { ...j.components, [k]: lines } };
    });
  };

  const unmarkLineReceived = (k, lineId) => setJob((j) => {
    const lines = getLines(j, k).map(l => l.id === lineId ? { ...l, received: null } : l);
    return { ...j, components: { ...j.components, [k]: lines } };
  });

  const handleInstalled = async () => {
    setSaving(true);
    const now = new Date().toISOString();
    const updatedJob = { ...job, installed_at: now };
    // Save wip job
    const cleaned = { ...updatedJob };
    ["deposit_date", "scheduled_install"].forEach((k) => { if (!cleaned[k]) cleaned[k] = null; });
    if (!Array.isArray(cleaned.minimised)) cleaned.minimised = [];
    if (!Array.isArray(cleaned.wip_notes)) cleaned.wip_notes = [];
    if (!cleaned.received || typeof cleaned.received !== "object" || Array.isArray(cleaned.received)) cleaned.received = {};
    if (!cleaned.component_meta || typeof cleaned.component_meta !== "object") cleaned.component_meta = {};
    cleaned.received = {};
    Object.keys(cleaned.components || {}).forEach(k => {
      const val = cleaned.components[k];
      if (Array.isArray(val)) { cleaned.received[k] = val.find(l => l.received)?.received || null; }
      else if (val) { cleaned.received[k] = (cleaned.received || {})[k] || null; }
    });
    delete cleaned.invoice_number;
    delete cleaned.invoice_value;
    await wipApi.upsert(cleaned);
    // Get files for this lead
    let files = [];
    try { files = await storage.list(lead.id); } catch (e) {}
    // Insert into completed_jobs
    await completedJobsApi.insert({
      lead_id: lead.id,
      installed_at: now,
      name: lead.name,
      phone: lead.phone,
      email: lead.email,
      street: lead.street,
      suburb: lead.suburb,
      postcode: lead.postcode,
      invoice_number: lead.invoice_number,
      invoice_value: lead.invoice_value,
      quote_value: lead.quote_value,
      products: updatedJob.products,
      components: updatedJob.components,
      received: updatedJob.received,
      deposit_date: updatedJob.deposit_date || null,
      scheduled_install: updatedJob.scheduled_install || null,
      notes: lead.notes ? (typeof lead.notes === "string" ? lead.notes : JSON.stringify(lead.notes)) : null,
      files: files.length ? JSON.stringify(files.map(f => f.name)) : null,
    });
    // Clear next_contact so lead doesn't appear in Follow Up
    await supabase.update(lead.id, { ...lead, next_contact: null, notes: lead.notes ? JSON.stringify(lead.notes) : null });
    await activityApi.log(lead.id, getCurrentUser(), "Installed", [lead.invoice_number ? `Invoice: ${lead.invoice_number}` : null, lead.invoice_value ? `Value: $${lead.invoice_value}` : null].filter(Boolean).join(" · ") || null);
    setSaving(false);
    setShowInstalled(false);
    onClose();
  };

  const [wipNoteText, setWipNoteText] = useState("");
  const addWipNote = () => {
    const text = wipNoteText.trim();
    if (!text) return;
    const note = { id: Date.now().toString(), date: new Date().toISOString().split("T")[0], user: getCurrentUser(), text };
    setJob(j => ({ ...j, wip_notes: [...(j.wip_notes || []), note] }));
    activityApi.log(lead.id, getCurrentUser(), "WIP note added", text.substring(0, 80)).catch(() => {});
    setWipNoteText("");
  };
  const removeWipNote = (id) => setJob(j => ({ ...j, wip_notes: (j.wip_notes || []).filter(n => n.id !== id) }));

  const wipHasChanges = originalJob && JSON.stringify(job) !== originalJob;

  const wipHandleClose = () => {
    if (wipHasChanges) setShowUnsaved(true);
    else onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    const cleaned = { ...job };
    ["deposit_date", "scheduled_install"].forEach((k) => {
      if (!cleaned[k]) cleaned[k] = null;
    });
    if (!Array.isArray(cleaned.minimised)) cleaned.minimised = [];
    if (!Array.isArray(cleaned.wip_notes)) cleaned.wip_notes = [];
    delete cleaned.invoice_number;
    delete cleaned.invoice_value;
    await wipApi.upsert(cleaned);
    const prevJob = originalJob ? JSON.parse(originalJob) : {};
    const wipChanges = [];
    if (cleaned.deposit_date && cleaned.deposit_date !== prevJob.deposit_date) wipChanges.push(`Deposit date: ${cleaned.deposit_date}`);
    if (cleaned.scheduled_install && cleaned.scheduled_install !== prevJob.scheduled_install) wipChanges.push(`Install date: ${cleaned.scheduled_install}`);
    if (cleaned.installer_request && cleaned.installer_request !== prevJob.installer_request) wipChanges.push(`Installer: ${cleaned.installer_request}`);
    if (cleaned.order_ready !== prevJob.order_ready) wipChanges.push(cleaned.order_ready ? "Order marked ready" : "Order ready unmarked");
    if (cleaned.installation_booked !== prevJob.installation_booked) wipChanges.push(cleaned.installation_booked ? "Installation booked" : "Installation booking removed");
    if (cleaned.balance_paid !== prevJob.balance_paid) wipChanges.push(cleaned.balance_paid ? "Balance paid" : "Balance payment removed");
    if (cleaned.customer_contacted !== prevJob.customer_contacted) wipChanges.push(cleaned.customer_contacted ? "Customer contacted" : "Customer contacted unmarked");
    // Save invoice fields back to lead
    const invoiceChanges = [];
    if (invoiceNumber && invoiceNumber !== lead.invoice_number) invoiceChanges.push(`Invoice #: ${invoiceNumber}`);
    if (invoiceValue && parseFloat(invoiceValue) !== lead.invoice_value) invoiceChanges.push(`Invoice value: $${invoiceValue}`);
    if (invoiceChanges.length) wipChanges.push(...invoiceChanges);
    const invoiceUpdates = {
      ...lead,
      invoice_number: invoiceNumber || null,
      invoice_value: invoiceValue ? parseFloat(invoiceValue) : null,
      notes: lead.notes ? (typeof lead.notes === "string" ? lead.notes : JSON.stringify(lead.notes)) : null,
    };
    await supabase.update(lead.id, invoiceUpdates);
    await activityApi.log(lead.id, getCurrentUser(), "WIP job updated", wipChanges.length ? wipChanges.join(" · ") : null);
    setSaving(false);
    onClose();
  };

  const sectionLabel = (text) => (
    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: "#94A3B8", textTransform: "uppercase", padding: "10px 0 6px", borderBottom: "1px solid #F1F5F9", marginBottom: 10 }}>{text}</div>
  );

  const Check = ({ label, value, onChange }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: value ? "#0F172A" : "#64748B", fontWeight: value ? 600 : 400 }}>
      <div onClick={onChange} style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${value ? "#8DC73F" : "#CBD5E1"}`, background: value ? "#8DC73F" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}>
        {value && <span style={{ color: "#fff", fontSize: 13, lineHeight: 1 }}>✓</span>}
      </div>
      {label}
    </label>
  );

  const DateCell = ({ label, value, onChange }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8" }}>{label}</div>
      <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
        style={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 6, padding: "5px 8px", color: value ? "#0F172A" : "#CBD5E1", background: value ? "#F0FDF4" : "#FAFAFA", width: 130 }} />
    </div>
  );

  const ProductCard = ({ k, label, suppliers }) => {
    const checked = !!(products[k] && products[k] !== false);
    const supplier = products[k] || "";
    return (
      <div style={{ border: `1px solid ${checked ? "#8DC73F" : "#E2E8F0"}`, borderRadius: 10, padding: "12px 14px", minWidth: 140, background: checked ? "#F7FBF0" : "#FAFAFA", transition: "all 0.15s" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: checked ? 10 : 0 }}>
          <div onClick={() => setProduct(k, checked ? null : suppliers[0])}
            style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? "#8DC73F" : "#CBD5E1"}`, background: checked ? "#8DC73F" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}>
            {checked && <span style={{ color: "#fff", fontSize: 11, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: checked ? "#0F172A" : "#64748B" }}>{label}</span>
        </label>
        {checked && (
          <select value={supplier} onChange={(e) => setProduct(k, e.target.value)}
            style={{ width: "100%", fontSize: 12, border: "1px solid #D1E8A0", borderRadius: 6, padding: "5px 8px", background: "#fff", color: "#0F172A", fontWeight: 600 }}>
            {suppliers.map(s => <option key={s}>{s}</option>)}
          </select>
        )}
      </div>
    );
  };

  if (loading) return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 40, color: "#64748B" }}>Loading job...</div>
    </div>
  );

  const products = job.products || {};

  return (
    <div className="ci-wip-wrap" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={wipHandleClose}>
      <div className="ci-wip-modal" style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 900, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="ci-modal-header" style={{ padding: "20px 28px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 10 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>{lead.name}</div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              {lead.invoice_number && <span style={{ marginRight: 12 }}>Invoice: <b style={{ color: "#475569" }}>{lead.invoice_number}</b></span>}
              {lead.quote_number && <span style={{ marginRight: 12 }}>Quote: <b style={{ color: "#475569" }}>{lead.quote_number}</b></span>}
              {lead.book_number && <span>Book: <b style={{ color: "#475569" }}>{lead.book_number}</b></span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={wipHandleClose} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        <div style={{ padding: "20px 28px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Customer details banner */}
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "12px 16px" }}>
            {lead.phone && <div><div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", letterSpacing: 1 }}>PHONE</div><div style={{ fontSize: 13, color: "#0F172A", fontWeight: 600 }}>{lead.phone}</div></div>}
            {lead.email && <div><div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", letterSpacing: 1 }}>EMAIL</div><div style={{ fontSize: 13, color: "#0F172A" }}>{lead.email}</div></div>}
            {lead.street && <div><div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", letterSpacing: 1 }}>ADDRESS</div><div style={{ fontSize: 13, color: "#0F172A" }}>{[lead.street, lead.suburb, lead.postcode].filter(Boolean).join(", ")}</div></div>}
            {lead.approved_date && <div><div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", letterSpacing: 1 }}>APPROVED</div><div style={{ fontSize: 13, color: "#10B981", fontWeight: 600 }}>{formatDate(lead.approved_date)}</div></div>}
          </div>

          {/* Lead Notes */}
          {(() => {
            const notes = Array.isArray(lead.notes) ? lead.notes : (typeof lead.notes === "string" ? (() => { try { return JSON.parse(lead.notes); } catch { return []; } })() : []);
            if (!notes.length) return null;
            const approvedDate = lead.approved_date ? new Date(lead.approved_date + "T00:00:00") : null;
            const preNotes = approvedDate ? notes.filter(n => !n.date || new Date(n.date + "T00:00:00") <= approvedDate) : notes;
            const postNotes = approvedDate ? notes.filter(n => n.date && new Date(n.date + "T00:00:00") > approvedDate) : [];
            const sorted = [...notes].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
            const sortedPre = [...preNotes].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
            const sortedPost = [...postNotes].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
            return (
              <div>
                {sectionLabel("Lead Notes")}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto" }}>
                  {sortedPre.map((n, i) => (
                    <div key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "8px 12px", border: "1px solid #E2E8F0" }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 3 }}>
                        {n.author && <span style={{ fontSize: 11, fontWeight: 700, color: "#8DC73F" }}>{n.author}</span>}
                        {n.date && <span style={{ fontSize: 11, color: "#94A3B8" }}>{new Date(n.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>}
                      </div>
                      <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{n.text}</div>
                    </div>
                  ))}
                  {approvedDate && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                      <div style={{ flex: 1, height: 1, background: "#8DC73F", opacity: 0.4 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#8DC73F", whiteSpace: "nowrap", letterSpacing: 0.5 }}>ORDER CONFIRMED {new Date(lead.approved_date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }).toUpperCase()}</span>
                      <div style={{ flex: 1, height: 1, background: "#8DC73F", opacity: 0.4 }} />
                    </div>
                  )}
                  {sortedPost.map((n, i) => (
                    <div key={i} style={{ background: "#F7FBF0", borderRadius: 8, padding: "8px 12px", border: "1px solid #D1E8A0" }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 3 }}>
                        {n.author && <span style={{ fontSize: 11, fontWeight: 700, color: "#8DC73F" }}>{n.author}</span>}
                        {n.date && <span style={{ fontSize: 11, color: "#94A3B8" }}>{new Date(n.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>}
                      </div>
                      <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{n.text}</div>
                    </div>
                  ))}
                  {!approvedDate && sorted.map((n, i) => (
                    <div key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "8px 12px", border: "1px solid #E2E8F0" }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 3 }}>
                        {n.author && <span style={{ fontSize: 11, fontWeight: 700, color: "#8DC73F" }}>{n.author}</span>}
                        {n.date && <span style={{ fontSize: 11, color: "#94A3B8" }}>{new Date(n.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>}
                      </div>
                      <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{n.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* WIP Notes */}
          {sectionLabel("WIP Notes")}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {(!job.wip_notes || job.wip_notes.length === 0) && (
                <div style={{ fontSize: 13, color: "#CBD5E1", padding: "4px 0" }}>No WIP notes yet</div>
              )}
              {(job.wip_notes || []).map(n => (
                <div key={n.id} style={{ background: "#F7FBF0", border: "1px solid #D1E8A0", borderRadius: 8, padding: "8px 12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      {n.user && <span style={{ fontSize: 11, fontWeight: 700, color: "#8DC73F" }}>{n.user}</span>}
                      {n.date && <span style={{ fontSize: 11, color: "#94A3B8" }}>{new Date(n.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>}
                    </div>
                    <button onClick={() => removeWipNote(n.id)} style={{ background: "none", border: "none", color: "#CBD5E1", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                  </div>
                  <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{n.text}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <textarea value={wipNoteText} onChange={e => setWipNoteText(e.target.value)}
                placeholder="Add a WIP note… (Shift+Enter to add)"
                rows={2}
                onKeyDown={e => { if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); addWipNote(); } }}
                style={{ flex: 1, fontSize: 13, border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 10px", resize: "none", fontFamily: "inherit", background: "#FAFAFA" }} />
              <button onClick={addWipNote} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, alignSelf: "flex-end" }}>+ Add</button>
            </div>
          </div>

          {/* Order Details */}
          {sectionLabel("Order Details")}
          <div className="ci-wip-order-row" style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8" }}>Invoice Number</div>
              <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="—"
                style={{ fontSize: 13, fontWeight: 600, border: "1px solid #E2E8F0", borderRadius: 6, padding: "5px 8px", minWidth: 120, background: "#FAFAFA", color: "#0F172A", fontFamily: "inherit" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8" }}>Invoice Value ($)</div>
              <input type="number" value={invoiceValue} onChange={e => setInvoiceValue(e.target.value)} placeholder="—"
                style={{ fontSize: 13, fontWeight: 600, border: "1px solid #E2E8F0", borderRadius: 6, padding: "5px 8px", minWidth: 120, background: "#FAFAFA", color: "#10B981", fontFamily: "inherit" }} />
            </div>
            <DateCell label="Deposit Date" value={job.deposit_date} onChange={(v) => setField("deposit_date", v)} />
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8" }}>Installer Request</div>
              <input value={job.installer_request || ""} onChange={(e) => setField("installer_request", e.target.value)} placeholder="—"
                style={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 6, padding: "5px 8px", width: 150, background: job.installer_request ? "#FAFAFA" : "#FAFAFA" }} />
            </div>
            <DateCell label="Scheduled Install" value={job.scheduled_install} onChange={(v) => setField("scheduled_install", v)} />

          </div>

          {/* Products */}
          {sectionLabel("Products")}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {(PRODUCTS || DEFAULT_PRODUCTS).map(({ key: k, label, suppliers }) => (
              <ProductCard key={k} k={k} label={label} suppliers={suppliers} />
            ))}
          </div>

          {/* Components */}
          {(() => {
            const activeProductKeys = (PRODUCTS || DEFAULT_PRODUCTS)
              .filter(p => !!(products[p.key] && products[p.key] !== false))
              .map(p => p.key);
            const anyProduct = activeProductKeys.length > 0;
            const seen = new Set();
            const visibleComponents = (COMPONENTS || DEFAULT_COMPONENTS).filter(c => {
              if (seen.has(c.key)) return false;
              const show = c.products.some(pk => activeProductKeys.includes(pk));
              if (show) seen.add(c.key);
              return show;
            });
            if (!anyProduct) return (
              <div style={{ color: "#CBD5E1", fontSize: 13, padding: "8px 0" }}>Select products above to see components</div>
            );
            // Collect all active line cards
            const activeCards = [];
            visibleComponents.forEach(({ key: k, label }) => {
              const lines = getLines(job, k);
              lines.forEach((line, idx) => {
                activeCards.push({ k, label, line, idx });
              });
            });

            // LineCard component
            const LineCard = ({ k, label, line, idx }) => {
              const lineReceived = !!line.received;
              const lineHasOrder = !!line.orderDate;
              let statusLabel, statusBg, statusColor, statusBorder;
              if (lineReceived) {
                statusLabel = "Received"; statusBg = "#F0FDF4"; statusColor = "#10B981"; statusBorder = "#86EFAC";
              } else if (lineHasOrder) {
                statusLabel = "Ordered"; statusBg = "#FFFBEB"; statusColor = "#D97706"; statusBorder = "#FCD34D";
              } else {
                statusLabel = "Not ordered"; statusBg = "#F8FAFC"; statusColor = "#94A3B8"; statusBorder = "#E2E8F0";
              }
              return (
                <div style={{ border: `1px solid ${statusBorder}`, borderRadius: 12, padding: "14px 16px", background: lineReceived ? "#F0FDF4" : "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{label}</span>
                      {idx > 0 && <span style={{ fontSize: 11, color: "#94A3B8", marginLeft: 6 }}>#{idx + 1}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, background: statusBg, color: statusColor, border: `1px solid ${statusBorder}`, borderRadius: 99, padding: "2px 8px" }}>{statusLabel}</span>
                      <button onClick={() => removeLine(k, line.id)} style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#EF4444", cursor: "pointer", fontSize: 12, padding: "2px 7px", borderRadius: 6, fontWeight: 700 }}>×</button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", marginBottom: 3 }}>Supplier</div>
                      <input key={line.id + "-supplier"} defaultValue={line.supplier || ""} onBlur={(e) => updateLine(k, line.id, "supplier", e.target.value)}
                        disabled={lineReceived} placeholder="—"
                        style={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 6, padding: "5px 8px", width: "100%", boxSizing: "border-box", background: lineReceived ? "#F0FDF4" : "#FAFAFA", fontFamily: "inherit", opacity: lineReceived ? 0.7 : 1 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", marginBottom: 3 }}>Item</div>
                      <input key={line.id + "-item"} defaultValue={line.item || ""} onBlur={(e) => updateLine(k, line.id, "item", e.target.value)}
                        disabled={lineReceived} placeholder="—"
                        style={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 6, padding: "5px 8px", width: "100%", boxSizing: "border-box", background: lineReceived ? "#F0FDF4" : "#FAFAFA", fontFamily: "inherit", opacity: lineReceived ? 0.7 : 1 }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", marginBottom: 3 }}>Order date</div>
                  <input type="date" value={line.orderDate || ""} onChange={(e) => updateLine(k, line.id, "orderDate", e.target.value)}
                    disabled={lineReceived}
                    style={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 6, padding: "5px 8px", width: "100%", boxSizing: "border-box", background: lineReceived ? "#F0FDF4" : "#FAFAFA", color: line.orderDate ? "#0F172A" : "#CBD5E1", marginBottom: 8, fontFamily: "inherit", opacity: lineReceived ? 0.7 : 1 }} />
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", marginBottom: 4 }}>Received by</div>
                  {lineReceived ? (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#10B981" }}>{line.received.user}</span>
                        <span style={{ fontSize: 12, color: "#64748B" }}> · {line.received.date ? new Date(line.received.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                      </div>
                      <button onClick={() => unmarkLineReceived(k, line.id)}
                        style={{ background: "none", border: "none", color: "#CBD5E1", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 0 0 8px" }}>↺</button>
                    </div>
                  ) : (
                    <button onClick={() => lineHasOrder && markLineReceived(k, line.id)} disabled={!lineHasOrder}
                      style={{ width: "100%", fontSize: 12, padding: "7px 0", background: lineHasOrder ? "#F8FAFC" : "#F1F5F9", border: `1px solid ${lineHasOrder ? "#E2E8F0" : "#F1F5F9"}`, borderRadius: 7, color: lineHasOrder ? "#475569" : "#CBD5E1", cursor: lineHasOrder ? "pointer" : "not-allowed", fontWeight: 600, fontFamily: "inherit" }}>
                      {lineHasOrder ? "Mark as received" : "Set order date first"}
                    </button>
                  )}
                </div>
              );
            };

            return <>
              {sectionLabel("Components — Order & Receiving")}
              {/* Active cards row */}
              {activeCards.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10, marginBottom: 10 }}>
                  {activeCards.map(({ k, label, line, idx }) => (
                    <LineCard key={`${k}-${line.id}`} k={k} label={label} line={line} idx={idx} />
                  ))}
                </div>
              )}
              {/* Add component chips row */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "10px 14px", background: "#F8FAFC", borderRadius: 10, border: "1px dashed #E2E8F0" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", alignSelf: "center", marginRight: 4 }}>ADD:</span>
                {visibleComponents.map(({ key: k, label }) => (
                  <button key={k} onClick={() => addLine(k)}
                    style={{ padding: "5px 12px", borderRadius: 99, border: "1px solid #CBD5E1", background: "#fff", color: "#475569", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>
                    + {label}
                  </button>
                ))}
              </div>
            </>;
          })()}

          {/* Completion */}
          {sectionLabel("Completion")}
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
            <Check label="Order Ready" value={job.order_ready} onChange={() => setField("order_ready", !job.order_ready)} />
            <Check label="Installation Booked" value={job.installation_booked} onChange={() => setField("installation_booked", !job.installation_booked)} />
            <Check label="Balance Paid" value={job.balance_paid} onChange={() => setField("balance_paid", !job.balance_paid)} />
            <Check label="Customer Contacted" value={job.customer_contacted} onChange={() => setField("customer_contacted", !job.customer_contacted)} />
            {!job.installed_at && (
              <button onClick={() => setShowInstalled(true)} disabled={!job.order_ready || !job.installation_booked || !job.balance_paid || !job.customer_contacted}
                style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: (job.order_ready && job.installation_booked && job.balance_paid && job.customer_contacted) ? "#8DC73F" : "#E2E8F0", color: (job.order_ready && job.installation_booked && job.balance_paid && job.customer_contacted) ? "#fff" : "#94A3B8", fontWeight: 700, cursor: (job.order_ready && job.installation_booked && job.balance_paid && job.customer_contacted) ? "pointer" : "not-allowed", fontSize: 14, fontFamily: "inherit" }}>
                Mark as Installed ✓
              </button>
            )}
            {job.installed_at && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 8, padding: "8px 14px" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#10B981" }}>✓ Installed</span>
                <span style={{ fontSize: 12, color: "#64748B" }}>{new Date(job.installed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
            )}
          </div>

        </div>
      </div>

      {showUnsaved && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>Unsaved changes</div>
            <div style={{ fontSize: 14, color: "#64748B", marginBottom: 24 }}>You have unsaved changes. What would you like to do?</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setShowUnsaved(false)}
                style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Keep Editing</button>
              <button onClick={() => { setShowUnsaved(false); onClose(); }}
                style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: "#EF4444", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Discard</button>
              <button onClick={() => { setShowUnsaved(false); handleSave(); }}
                style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Save & Close</button>
            </div>
          </div>
        </div>
      )}
      {showInstalled && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 400, width: "100%", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>Mark as Installed?</div>
            <div style={{ fontSize: 14, color: "#64748B", marginBottom: 6 }}>This will move <strong>{lead.name}</strong> out of WIP and into Completed Customers.</div>
            <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 24 }}>All lead and job data will be saved to the completed records.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setShowInstalled(false)}
                style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
              <button onClick={handleInstalled} disabled={saving}
                style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                {saving ? "Saving…" : "Confirm Installed ✓"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── WIP Page ─────────────────────────────────────────────────────────────────
const WIPPage = ({ leads, onWipJobsChange }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [wipJobs, setWipJobs] = useState({});
  const wonLeads = leads
    .filter(l => l.status === "Approved" && !wipJobs[l.id]?.installed_at)
    .slice()
    .sort((a, b) => {
      const da = wipJobs[a.id]?.deposit_date;
      const db = wipJobs[b.id]?.deposit_date;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return new Date(da) - new Date(db);
    });
  const totalWon = wonLeads.reduce((s, l) => s + (Number(l.invoice_value || l.quote_value) || 0), 0);

  const loadWipJobs = () => {
    wipApi.getAll().then((jobs) => {
      const map = {};
      (jobs || []).forEach((j) => { map[j.lead_id] = j; });
      setWipJobs(map);
      if (onWipJobsChange) onWipJobsChange(map);
    });
  };

  useEffect(() => { loadWipJobs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getJobLines = (j) => {
    const minimised = j.minimised || [];
    let total = 0, ordered = 0, receivedCount = 0;
    Object.entries(j.components || {}).forEach(([k, val]) => {
      if (minimised.includes(k) || !val) return;
      const lines = Array.isArray(val)
        ? val
        : [{ orderDate: val, received: (j.received || {})[k] || null }];
      lines.forEach(l => {
        // Count all lines that exist in the array — they were deliberately added via chip
        // Only skip the legacy "empty placeholder" which has no id
        if (!l.id && !l.orderDate && !l.supplier && !l.item && !l.received) return;
        total++;
        if (l.orderDate) { ordered++; if (l.received) receivedCount++; }
      });
    });
    return { total, ordered, receivedCount };
  };

  const getProgress = (leadId) => {
    const j = wipJobs[leadId];
    if (!j) return 0;
    const { total, receivedCount } = getJobLines(j);
    const compProgress = total > 0 ? (receivedCount / total) * 60 : 0;
    const checks = [j.order_ready, j.installation_booked, j.balance_paid, j.customer_contacted];
    const checkProgress = (checks.filter(Boolean).length / checks.length) * 40;
    return Math.round(compProgress + checkProgress);
  };

  const getComponentCount = (leadId) => {
    const j = wipJobs[leadId];
    if (!j || !j.components) return { received: 0, ordered: 0 };
    const { total, receivedCount } = getJobLines(j);
    return { received: receivedCount, total };
  };

  const getInstallDate = (leadId) => wipJobs[leadId]?.scheduled_install;

  const { products: PRODUCTS } = useSettings() || {
    products: DEFAULT_PRODUCTS,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}>WIP</div>
        <div style={{ fontSize: 14, color: "#64748B", marginTop: 2 }}>Track approved jobs from order to installation</div>
      </div>
      <div className="ci-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
        <StatCard label="Active Jobs" value={wonLeads.length} color="#10B981" />
        <StatCard label="Total Invoice Value" value={formatCurrency(totalWon)} color="#8DC73F" />
        <StatCard label="Avg Job Value" value={wonLeads.length ? formatCurrency(Math.round(totalWon / wonLeads.length)) : "—"} color="#8B5CF6" />
      </div>

      {selectedLead && <WIPModal lead={selectedLead} onClose={() => { setSelectedLead(null); loadWipJobs(); }} />}

      <div className="ci-leads-table-wrap" style={{ background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0" }}>
        <table className="ci-leads-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F8FAFC" }}>
              {["Customer", "Invoice", "Invoice Value", "Deposit Date", "Days Since Deposit", "Products", "Components", "Install Date", "Progress", "Completion"].map((h) => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.1, textTransform: "uppercase", borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {wonLeads.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 15 }}>No active jobs yet.</td></tr>
            ) : wonLeads.map((lead, i) => {
              const j = wipJobs[lead.id] || {};
              const progress = getProgress(lead.id);
              const { received, total: compTotal } = getComponentCount(lead.id);
              const installDate = getInstallDate(lead.id);
              const products = j.products || {};
              const activeProducts = (PRODUCTS || DEFAULT_PRODUCTS).filter(p => products[p.key] && products[p.key] !== false).map(p => [p.key, p.label]);

              return (
                <tr key={lead.id} onClick={() => setSelectedLead(lead)}
                  style={{ borderBottom: i < wonLeads.length - 1 ? "1px solid #F1F5F9" : "none", cursor: "pointer" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{lead.name}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>{lead.suburb || ""}</div>
                  </td>
                  <td style={{ padding: "13px 16px", fontSize: 13, color: "#475569", fontWeight: 600 }}>{lead.invoice_number || "—"}</td>
                  <td style={{ padding: "13px 16px", fontSize: 13, color: "#475569", fontWeight: 600 }}>{lead.invoice_value ? formatCurrency(lead.invoice_value) : "—"}</td>
                  {(() => {
                    const dep = j.deposit_date;
                    const days = dep ? Math.floor((new Date() - new Date(dep)) / (1000 * 60 * 60 * 24)) : null;
                    const urgency = days === null ? null : days > 60 ? "red" : days > 30 ? "amber" : "green";
                    const urgencyColors = { red: { bg: "#FEF2F2", text: "#DC2626" }, amber: { bg: "#FFFBEB", text: "#D97706" }, green: { bg: "#F0FDF4", text: "#10B981" } };
                    const c = urgency ? urgencyColors[urgency] : null;
                    return <>
                      <td style={{ padding: "13px 16px", fontSize: 13, color: dep ? "#0F172A" : "#CBD5E1", whiteSpace: "nowrap" }}>{dep ? formatDate(dep) : "—"}</td>
                      <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                        {days !== null
                          ? <span style={{ fontSize: 12, fontWeight: 700, background: c.bg, color: c.text, padding: "3px 10px", borderRadius: 20 }}>{days}d</span>
                          : <span style={{ color: "#CBD5E1", fontSize: 13 }}>—</span>}
                      </td>
                    </>;
                  })()}
                  <td style={{ padding: "13px 16px" }}>
                    {activeProducts.length === 0 ? <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span> :
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {activeProducts.map(([k, label]) => (
                          <span key={k} style={{ fontSize: 11, background: "#FFF7ED", color: "#C2410C", borderRadius: 5, padding: "2px 7px", fontWeight: 600 }}>{label}: {products[k]}</span>
                        ))}
                      </div>
                    }
                  </td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: received > 0 && received === compTotal ? "#10B981" : compTotal > 0 ? "#8DC73F" : "#CBD5E1" }}>
                      {compTotal > 0 ? (
                        <span title={`${received} received of ${compTotal} total`}>
                          {received}/{compTotal} rcvd
                        </span>
                      ) : <span style={{ color: "#CBD5E1" }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: "13px 16px", fontSize: 13, color: installDate ? "#0F172A" : "#CBD5E1", whiteSpace: "nowrap" }}>
                    {installDate ? formatDate(installDate) : "—"}
                  </td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: "#F1F5F9", borderRadius: 99, overflow: "hidden", minWidth: 60 }}>
                        <div style={{ height: "100%", width: `${progress}%`, background: progress === 100 ? "#10B981" : "#8DC73F", borderRadius: 99 }} />
                      </div>
                      <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>{progress}%</span>
                    </div>
                  </td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {[["📦", j.order_ready],["📅", j.installation_booked],["💰", j.balance_paid],["📞", j.customer_contacted]].map(([icon, val], idx) => (
                        <span key={idx} style={{ fontSize: 15, opacity: val ? 1 : 0.2 }}>{icon}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ─── Admin Page ───────────────────────────────────────────────────────────────
const AdminSaveBtn = ({ saving, saved, k, onClick }) => (
  <button onClick={onClick} disabled={saving === k}
    style={{ padding: "7px 16px", borderRadius: 7, border: "none", background: saved === k ? "#10B981" : "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, transition: "background 0.3s" }}>
    {saved === k ? "✓ Saved" : saving === k ? "Saving…" : "Save"}
  </button>
);

const AdminSimpleList = ({ label, items, stateKey, dbKey, allowBlank, saving, saved, onUpdate, onAdd, onRemove, onSave }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "20px 24px", marginBottom: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{label}</div>
      <AdminSaveBtn saving={saving} saved={saved} k={dbKey} onClick={onSave} />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input value={item} onChange={(e) => onUpdate(i, e.target.value)} placeholder="Enter value"
            style={{ flex: 1, padding: "7px 10px", borderRadius: 7, border: "1px solid #E2E8F0", fontSize: 13, fontFamily: "inherit" }} />
          <button onClick={() => onRemove(i)} style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid #E2E8F0", background: "#fff", color: "#EF4444", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>×</button>
        </div>
      ))}
      <button onClick={onAdd} style={{ padding: "7px 12px", borderRadius: 7, border: "1px dashed #CBD5E1", background: "#FAFAFA", color: "#64748B", cursor: "pointer", fontSize: 13, fontWeight: 600, marginTop: 2 }}>+ Add</button>
    </div>
  </div>
);

const AdminProductsEditor = ({ products, saving, saved, onSave, onUpdate }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "20px 24px", marginBottom: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Products & Suppliers</div>
      <AdminSaveBtn saving={saving} saved={saved} k="products" onClick={onSave} />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {products.map((p, i) => (
        <div key={p.key} style={{ border: "1px solid #F1F5F9", borderRadius: 10, padding: "12px 16px", background: "#FAFAFA" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <input value={p.label} onChange={(e) => onUpdate("label", i, null, e.target.value)} placeholder="Product name"
              style={{ flex: 1, padding: "6px 10px", borderRadius: 7, border: "1px solid #E2E8F0", fontSize: 13, fontWeight: 600, fontFamily: "inherit" }} />
            <button onClick={() => onUpdate("removeProduct", i)} style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #E2E8F0", background: "#fff", color: "#EF4444", cursor: "pointer", fontSize: 14 }}>×</button>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", marginBottom: 6, letterSpacing: 1 }}>SUPPLIERS</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {p.suppliers.map((s, j) => (
              <div key={j} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input value={s} onChange={(e) => onUpdate("supplier", i, j, e.target.value)} placeholder="Supplier"
                  style={{ width: 140, padding: "5px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12, fontFamily: "inherit" }} />
                <button onClick={() => onUpdate("removeSupplier", i, j)} style={{ background: "none", border: "none", color: "#CBD5E1", cursor: "pointer", fontSize: 14 }}>×</button>
              </div>
            ))}
            <button onClick={() => onUpdate("addSupplier", i)} style={{ padding: "5px 10px", borderRadius: 6, border: "1px dashed #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer", fontSize: 12 }}>+ Supplier</button>
          </div>
        </div>
      ))}
      <button onClick={() => onUpdate("addProduct")} style={{ padding: "8px 12px", borderRadius: 7, border: "1px dashed #CBD5E1", background: "#FAFAFA", color: "#64748B", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ Add Product</button>
    </div>
  </div>
);

const AdminComponentsEditor = ({ components, products, saving, saved, onSave, onUpdate }) => {
  const productKeys = products.map(p => p.key);
  const productLabels = Object.fromEntries(products.map(p => [p.key, p.label]));
  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "20px 24px", marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Components & Product Mapping</div>
        <AdminSaveBtn saving={saving} saved={saved} k="components" onClick={onSave} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {components.map((c, i) => (
          <div key={c.key} style={{ border: "1px solid #F1F5F9", borderRadius: 10, padding: "10px 14px", background: "#FAFAFA", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input value={c.label} onChange={(e) => onUpdate("label", i, e.target.value)} placeholder="Component name"
              style={{ width: 130, padding: "5px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 13, fontWeight: 600, fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
              {productKeys.map(pk => (
                <label key={pk} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 12, color: c.products.includes(pk) ? "#0F172A" : "#94A3B8" }}>
                  <input type="checkbox" checked={c.products.includes(pk)} onChange={() => onUpdate("toggle", i, pk)} style={{ cursor: "pointer" }} />
                  {productLabels[pk] || pk}
                </label>
              ))}
            </div>
            <button onClick={() => onUpdate("remove", i)} style={{ background: "none", border: "none", color: "#CBD5E1", cursor: "pointer", fontSize: 16 }}>×</button>
          </div>
        ))}
        <button onClick={() => onUpdate("add")} style={{ padding: "8px 12px", borderRadius: 7, border: "1px dashed #CBD5E1", background: "#FAFAFA", color: "#64748B", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ Add Component</button>
      </div>
    </div>
  );
};


// ─── Backup Export ────────────────────────────────────────────────────────────
const BackupExport = () => {
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState(null);

  const cell = (val) => {
    if (val === null || val === undefined) return "";
    return `"${String(val).replace(/"/g, '""')}"`;
  };

  // Flatten JSON fields into readable text
  const flattenNotes = (notes) => {
    if (!notes) return "";
    const arr = typeof notes === "string" ? JSON.parse(notes) : notes;
    if (!Array.isArray(arr)) return "";
    return arr.map(n => `[${n.date || ""}${n.author ? " - " + n.author : ""}] ${n.text || ""}`).join(" | ");
  };

  const flattenProducts = (products) => {
    if (!products) return "";
    const obj = typeof products === "string" ? JSON.parse(products) : products;
    return Object.entries(obj || {}).filter(([, v]) => v).map(([k, v]) => {
      if (typeof v === "object" && v.supplier) return `${k} (${v.supplier})`;
      return k;
    }).join(", ");
  };

  const flattenComponents = (components) => {
    if (!components) return "";
    const obj = typeof components === "string" ? JSON.parse(components) : components;
    return Object.entries(obj || {}).map(([k, v]) => {
      if (!v) return null;
      if (Array.isArray(v)) {
        return v.map(l => `${k}: ${[l.item, l.supplier, l.orderDate ? "ordered " + l.orderDate : "not ordered", l.received ? "received" : "pending"].filter(Boolean).join(", ")}`).join(" | ");
      }
      return `${k}: ${v}`;
    }).filter(Boolean).join(" | ");
  };

  const flattenWipNotes = (notes) => {
    if (!notes) return "";
    const arr = typeof notes === "string" ? JSON.parse(notes) : notes;
    if (!Array.isArray(arr)) return "";
    return arr.map(n => `[${n.date || ""}${n.user ? " - " + n.user : ""}] ${n.text || ""}`).join(" | ");
  };

  const flattenReferrals = (referrals) => {
    if (!referrals) return "";
    const arr = typeof referrals === "string" ? JSON.parse(referrals) : referrals;
    if (!Array.isArray(arr)) return "";
    return arr.map(r => `[${r.date || ""}] ${r.client || ""}${r.note ? " - " + r.note : ""}${r.user ? " (logged by " + r.user + ")" : ""}`).join(" | ");
  };

  const fetchTable = async (table) => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=*&limit=10000`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
    });
    if (!res.ok) throw new Error(`Failed to fetch ${table}`);
    return res.json();
  };

  const buildLeadsCSV = (rows) => {
    const cols = ["id","created_at","name","phone","email","street","suburb","postcode","source","status","warmth","first_contact","next_contact","quote_value","quote_number","book_number","cm_date","quote_sent_date","brief_note","approved_date","rejected_date","invoice_number","invoice_value","stage","notes"];
    const header = cols.join(",");
    const body = rows.map(r => [
      cell(r.id), cell(r.created_at), cell(r.name), cell(r.phone), cell(r.email),
      cell(r.street), cell(r.suburb), cell(r.postcode), cell(r.source), cell(r.status),
      cell(r.warmth), cell(r.first_contact), cell(r.next_contact), cell(r.quote_value),
      cell(r.quote_number), cell(r.book_number), cell(r.cm_date), cell(r.quote_sent_date),
      cell(r.brief_note), cell(r.approved_date), cell(r.rejected_date),
      cell(r.invoice_number), cell(r.invoice_value), cell(r.stage),
      cell(flattenNotes(r.notes)),
    ].join(",")).join("\n");
    return header + "\n" + body;
  };

  const buildWipJobsCSV = (rows) => {
    const cols = ["id","created_at","lead_id","deposit_date","installer_request","scheduled_install","order_ready","installation_booked","balance_paid","customer_contacted","installed_at","products","components","wip_notes"];
    const header = cols.join(",");
    const body = rows.map(r => [
      cell(r.id), cell(r.created_at), cell(r.lead_id), cell(r.deposit_date),
      cell(r.installer_request), cell(r.scheduled_install), cell(r.order_ready),
      cell(r.installation_booked), cell(r.balance_paid), cell(r.customer_contacted),
      cell(r.installed_at),
      cell(flattenProducts(r.products)),
      cell(flattenComponents(r.components)),
      cell(flattenWipNotes(r.wip_notes)),
    ].join(",")).join("\n");
    return header + "\n" + body;
  };

  const buildCompletedJobsCSV = (rows) => {
    const cols = ["id","created_at","installed_at","lead_id","name","phone","email","street","suburb","postcode","invoice_number","invoice_value","quote_value","deposit_date","scheduled_install","products","components","notes"];
    const header = cols.join(",");
    const body = rows.map(r => [
      cell(r.id), cell(r.created_at), cell(r.installed_at), cell(r.lead_id),
      cell(r.name), cell(r.phone), cell(r.email), cell(r.street), cell(r.suburb),
      cell(r.postcode), cell(r.invoice_number), cell(r.invoice_value), cell(r.quote_value),
      cell(r.deposit_date), cell(r.scheduled_install),
      cell(flattenProducts(r.products)),
      cell(flattenComponents(r.components)),
      cell(flattenNotes(r.notes)),
    ].join(",")).join("\n");
    return header + "\n" + body;
  };

  const buildPartnersCSV = (rows) => {
    const cols = ["id","created_at","company_name","contact_name","phone","email","suburb","office_address","trade_type","jobs_referred","last_contacted","next_contact","notes","referrals"];
    const header = cols.join(",");
    const body = rows.map(r => [
      cell(r.id), cell(r.created_at), cell(r.company_name), cell(r.contact_name),
      cell(r.phone), cell(r.email), cell(r.suburb), cell(r.office_address),
      cell(r.trade_type), cell(r.jobs_referred), cell(r.last_contacted), cell(r.next_contact),
      cell(flattenNotes(r.notes)),
      cell(flattenReferrals(r.referrals)),
    ].join(",")).join("\n");
    return header + "\n" + body;
  };

  const buildActivityCSV = (rows) => {
    const cols = ["id","created_at","lead_id","user_name","event","detail"];
    const header = cols.join(",");
    const body = rows.map(r => [
      cell(r.id), cell(r.created_at), cell(r.lead_id),
      cell(r.user_name), cell(r.event), cell(r.detail),
    ].join(",")).join("\n");
    return header + "\n" + body;
  };

  const handleExport = async () => {
    setExporting(true);
    setStatus(null);
    try {
      if (!window.JSZip) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      const timestamp = new Date().toISOString().split("T")[0];
      const folderName = `curtain-ideas-backup-${timestamp}`;
      const zip = new window.JSZip();
      const folder = zip.folder(folderName);

      const tables = [
        { name: "leads", builder: buildLeadsCSV },
        { name: "wip_jobs", builder: buildWipJobsCSV },
        { name: "completed_jobs", builder: buildCompletedJobsCSV },
        { name: "partners", builder: buildPartnersCSV },
        { name: "activity_log", builder: buildActivityCSV },
      ];

      for (let i = 0; i < tables.length; i++) {
        const { name, builder } = tables[i];
        setStatus({ table: name, done: i, total: tables.length });
        const rows = await fetchTable(name);
        folder.file(`${name}.csv`, builder(rows));
      }

      setStatus({ table: "zipping", done: tables.length, total: tables.length });
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${folderName}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);

      setStatus("done");
      setTimeout(() => setStatus(null), 4000);
    } catch (e) {
      console.error("Backup error:", e);
      setStatus("error");
      setTimeout(() => setStatus(null), 4000);
    }
    setExporting(false);
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Database Backup</div>
          <div style={{ fontSize: 13, color: "#64748B" }}>Downloads all data as separate CSV files — leads, WIP jobs, completed jobs, partners and activity log.</div>
        </div>
        <button onClick={handleExport} disabled={exporting}
          style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "#1C2E0F", color: "#8DC73F", fontWeight: 700, fontSize: 13, cursor: exporting ? "not-allowed" : "pointer", opacity: exporting ? 0.7 : 1, display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit", flexShrink: 0, marginLeft: 16 }}>
          {exporting ? "Exporting…" : "📥 Export Backup"}
        </button>
      </div>
      {status && status !== "done" && status !== "error" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
          <div style={{ flex: 1, height: 6, background: "#F1F5F9", borderRadius: 99, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "#8DC73F", borderRadius: 99, width: `${(status.done / status.total) * 100}%`, transition: "width 0.3s" }} />
          </div>
          <span style={{ fontSize: 12, color: "#64748B", whiteSpace: "nowrap" }}>{status.table === "zipping" ? "Creating zip file…" : `Fetching ${status.table}…`}</span>
        </div>
      )}
      {status === "done" && (
        <div style={{ fontSize: 13, color: "#10B981", fontWeight: 600, marginTop: 8 }}>✓ Backup complete — {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</div>
      )}
      {status === "error" && (
        <div style={{ fontSize: 13, color: "#EF4444", fontWeight: 600, marginTop: 8 }}>✗ Export failed — check your connection and try again</div>
      )}
    </div>
  );
};


// ─── CSV Import ───────────────────────────────────────────────────────────────
const TABLE_SCHEMAS = {
  leads: {
    label: "Leads",
    cols: ["name","phone","email","street","suburb","postcode","source","status","warmth","first_contact","next_contact","quote_value","quote_number","book_number","cm_date","quote_sent_date","brief_note","approved_date","rejected_date","invoice_number","invoice_value","stage"],
    dateFields: ["first_contact","next_contact","cm_date","quote_sent_date","approved_date","rejected_date"],
    numericFields: ["quote_value","invoice_value"],
    description: "Append new leads. Existing leads are not modified.",
  },
  wip_jobs: {
    label: "WIP Jobs",
    cols: ["lead_id","deposit_date","installer_request","scheduled_install","order_ready","installation_booked","balance_paid","customer_contacted"],
    dateFields: ["deposit_date","scheduled_install"],
    numericFields: [],
    description: "Requires a valid lead_id. Use for restoring WIP data.",
  },
  completed_jobs: {
    label: "Completed Jobs",
    cols: ["lead_id","name","phone","email","street","suburb","postcode","invoice_number","invoice_value","quote_value","deposit_date","scheduled_install","installed_at"],
    dateFields: ["deposit_date","scheduled_install","installed_at"],
    numericFields: ["invoice_value","quote_value"],
    description: "Restore completed job records. Requires lead_id.",
  },
  partners: {
    label: "Partners",
    cols: ["company_name","contact_name","phone","email","suburb","office_address","trade_type","jobs_referred","last_contacted","next_contact"],
    dateFields: ["last_contacted","next_contact"],
    numericFields: ["jobs_referred"],
    description: "Append new partners or restore from backup.",
  },
};

const CSVImport = () => {
  const [selectedTable, setSelectedTable] = useState("leads");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = React.useRef(null);

  const schema = TABLE_SCHEMAS[selectedTable];

  const FIELD_DESCRIPTIONS = {
    leads: [
      { field: "name", type: "Text", description: "Customer or project name", options: "e.g. Smith Residence" },
      { field: "phone", type: "Text", description: "Contact phone number", options: "" },
      { field: "email", type: "Text", description: "Contact email address", options: "" },
      { field: "street", type: "Text", description: "Street address", options: "e.g. 42 Example St" },
      { field: "suburb", type: "Text", description: "Suburb", options: "" },
      { field: "postcode", type: "Text", description: "Postcode", options: "" },
      { field: "source", type: "Dropdown", description: "How the lead was acquired", options: "Referral, Previous Customer, Facebook, Website Form, Phone Call, Walk In, Shows & Exhibition, Trade Customer, Call & Online, Trade & Repeat" },
      { field: "status", type: "Dropdown", description: "Current lead status", options: "In Progress, Approved, Rejected, Uncontactable, Delayed" },
      { field: "warmth", type: "Dropdown", description: "Likelihood of converting", options: "Unlikely, Likely, Verbally Approved" },
      { field: "first_contact", type: "Date (YYYY-MM-DD)", description: "Date first contacted", options: "e.g. 2026-01-15" },
      { field: "next_contact", type: "Date (YYYY-MM-DD)", description: "Scheduled follow-up date", options: "e.g. 2026-02-01" },
      { field: "quote_value", type: "Number", description: "Estimated quote value in dollars", options: "e.g. 4500" },
      { field: "quote_number", type: "Text", description: "Quote reference number", options: "e.g. Q-1042" },
      { field: "book_number", type: "Text", description: "Book reference number", options: "" },
      { field: "cm_date", type: "Date (YYYY-MM-DD)", description: "Check measure appointment date", options: "" },
      { field: "quote_sent_date", type: "Date (YYYY-MM-DD)", description: "Date quote was sent to customer", options: "" },
      { field: "brief_note", type: "Text", description: "Short one-line summary of the lead", options: "" },
      { field: "approved_date", type: "Date (YYYY-MM-DD)", description: "Date the job was approved", options: "" },
      { field: "rejected_date", type: "Date (YYYY-MM-DD)", description: "Date the lead was rejected", options: "" },
      { field: "invoice_number", type: "Text", description: "Invoice reference number", options: "e.g. INV-1042" },
      { field: "invoice_value", type: "Number", description: "Final invoice value in dollars", options: "e.g. 5200" },
      { field: "stage", type: "Text", description: "Custom stage label", options: "" },
    ],
    wip_jobs: [
      { field: "lead_id", type: "Number", description: "ID of the related lead (required)", options: "Must match an existing lead id" },
      { field: "deposit_date", type: "Date (YYYY-MM-DD)", description: "Date deposit was received", options: "" },
      { field: "installer_request", type: "Text", description: "Preferred installer name or notes", options: "" },
      { field: "scheduled_install", type: "Date (YYYY-MM-DD)", description: "Scheduled installation date", options: "" },
      { field: "order_ready", type: "Boolean", description: "Whether the order is ready", options: "true or false" },
      { field: "installation_booked", type: "Boolean", description: "Whether installation is booked", options: "true or false" },
      { field: "balance_paid", type: "Boolean", description: "Whether the balance has been paid", options: "true or false" },
      { field: "customer_contacted", type: "Boolean", description: "Whether the customer has been contacted", options: "true or false" },
    ],
    completed_jobs: [
      { field: "lead_id", type: "Number", description: "ID of the related lead", options: "Must match an existing lead id" },
      { field: "name", type: "Text", description: "Customer name at time of completion", options: "" },
      { field: "phone", type: "Text", description: "Customer phone at time of completion", options: "" },
      { field: "email", type: "Text", description: "Customer email at time of completion", options: "" },
      { field: "street", type: "Text", description: "Street address", options: "" },
      { field: "suburb", type: "Text", description: "Suburb", options: "" },
      { field: "postcode", type: "Text", description: "Postcode", options: "" },
      { field: "invoice_number", type: "Text", description: "Invoice reference number", options: "" },
      { field: "invoice_value", type: "Number", description: "Final invoice value in dollars", options: "" },
      { field: "quote_value", type: "Number", description: "Original quote value in dollars", options: "" },
      { field: "deposit_date", type: "Date (YYYY-MM-DD)", description: "Date deposit was received", options: "" },
      { field: "scheduled_install", type: "Date (YYYY-MM-DD)", description: "Scheduled installation date", options: "" },
      { field: "installed_at", type: "Date (YYYY-MM-DD)", description: "Actual installation completion date", options: "" },
    ],
    partners: [
      { field: "company_name", type: "Text", description: "Business or company name", options: "" },
      { field: "contact_name", type: "Text", description: "Primary contact person", options: "" },
      { field: "phone", type: "Text", description: "Contact phone number", options: "" },
      { field: "email", type: "Text", description: "Contact email address", options: "" },
      { field: "suburb", type: "Text", description: "Partner suburb", options: "" },
      { field: "office_address", type: "Text", description: "Full office address", options: "e.g. 42 Example St, Suburb VIC 3000" },
      { field: "trade_type", type: "Dropdown", description: "Type of trade relationship", options: "Interior Designer, Builder, Architect, Decorator, Real Estate Agent, Property Manager, Other" },
      { field: "jobs_referred", type: "Number", description: "Total number of referrals from this partner", options: "e.g. 5" },
      { field: "last_contacted", type: "Date (YYYY-MM-DD)", description: "Date last contacted", options: "" },
      { field: "next_contact", type: "Date (YYYY-MM-DD)", description: "Scheduled next contact date", options: "" },
    ],
  };

  const downloadTemplate = async () => {
    // Load SheetJS dynamically
    if (!window.XLSX) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });
    }

    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Data template with example row
    const exampleRow = schema.cols.reduce((row, c) => {
      if (schema.dateFields.includes(c)) row[c] = "2026-01-15";
      else if (schema.numericFields.includes(c)) row[c] = 0;
      else if (c === "status") row[c] = "In Progress";
      else if (c === "source") row[c] = "Referral";
      else if (c === "trade_type") row[c] = "Interior Designer";
      else if (c.includes("_id")) row[c] = 123;
      else if (c === "order_ready" || c === "balance_paid" || c === "installation_booked" || c === "customer_contacted") row[c] = false;
      else row[c] = "";
      return row;
    }, {});

    const dataWs = XLSX.utils.json_to_sheet([exampleRow], { header: schema.cols });

    // Style header row
    const range = XLSX.utils.decode_range(dataWs["!ref"]);
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!dataWs[addr]) continue;
      dataWs[addr].s = { font: { bold: true }, fill: { fgColor: { rgb: "1C2E0F" } }, fontColor: { rgb: "8DC73F" } };
    }

    // Auto column widths
    dataWs["!cols"] = schema.cols.map(c => ({ wch: Math.max(c.length + 2, 16) }));
    XLSX.utils.book_append_sheet(wb, dataWs, "Data");

    // Sheet 2 — Field descriptions
    const descriptions = FIELD_DESCRIPTIONS[selectedTable] || [];
    const descRows = descriptions.map(d => ({
      "Field": d.field,
      "Type": d.type,
      "Description": d.description,
      "Options / Example": d.options,
    }));
    const descWs = XLSX.utils.json_to_sheet(descRows, { header: ["Field", "Type", "Description", "Options / Example"] });
    descWs["!cols"] = [{ wch: 22 }, { wch: 22 }, { wch: 45 }, { wch: 55 }];

    // Bold description headers
    ["A1","B1","C1","D1"].forEach(addr => {
      if (descWs[addr]) descWs[addr].s = { font: { bold: true } };
    });

    XLSX.utils.book_append_sheet(wb, descWs, "Field Guide");

    XLSX.writeFile(wb, `template_${selectedTable}.xlsx`);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
    return lines.slice(1).map(line => {
      const vals = [];
      let inQuote = false, cur = "";
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuote = !inQuote; }
        else if (ch === "," && !inQuote) { vals.push(cur); cur = ""; }
        else { cur += ch; }
      }
      vals.push(cur);
      const row = {};
      headers.forEach((h, i) => { row[h] = vals[i]?.trim() || null; });
      return row;
    });
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    const text = await f.text();
    const rows = parseCSV(text);
    setPreview({ rows, total: rows.length });
  };

  const cleanRow = (row) => {
    const cleaned = { ...row };
    schema.dateFields.forEach(k => { if (!cleaned[k] || cleaned[k] === "") cleaned[k] = null; });
    schema.numericFields.forEach(k => { cleaned[k] = cleaned[k] ? parseFloat(cleaned[k]) : null; });
    // Remove id and created_at — let Supabase generate them
    delete cleaned.id;
    delete cleaned.created_at;
    // Remove empty strings
    Object.keys(cleaned).forEach(k => { if (cleaned[k] === "") cleaned[k] = null; });
    return cleaned;
  };

  const handleImport = async () => {
    if (!preview || !preview.rows.length) return;
    setImporting(true);
    setResult(null);
    let success = 0, failed = 0;
    const errors = [];

    // Insert in batches of 50
    const rows = preview.rows.map(cleanRow);
    const batchSize = 50;
    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
      try {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/${selectedTable}`, {
          method: "POST",
          headers: {
            apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`,
            "Content-Type": "application/json", Prefer: "return=minimal",
          },
          body: JSON.stringify(batch),
        });
        if (res.ok) { success += batch.length; }
        else {
          const err = await res.text();
          failed += batch.length;
          errors.push(`Rows ${i+1}-${i+batch.length}: ${err}`);
        }
      } catch (e) {
        failed += batch.length;
        errors.push(e.message);
      }
    }

    setResult({ success, failed, errors });
    setImporting(false);
    setPreview(null);
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Import / Restore Data</div>
      <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>Upload a CSV to append rows to any table. Download a template first to see the required format.</div>

      {/* Table selector */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Table</div>
          <select value={selectedTable} onChange={e => { setSelectedTable(e.target.value); setPreview(null); setFile(null); setResult(null); }} style={inputStyle}>
            {Object.entries(TABLE_SCHEMAS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <button onClick={downloadTemplate}
          style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#F8FAFC", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          📄 Download Template
        </button>
      </div>

      <div style={{ fontSize: 12, color: "#8DC73F", fontWeight: 600, marginBottom: 12 }}>{schema.description}</div>

      {/* File upload */}
      <div style={{ border: "2px dashed #E2E8F0", borderRadius: 8, padding: "20px", textAlign: "center", marginBottom: 12, background: "#FAFAFA", cursor: "pointer" }}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { const evt = { target: { files: [f] } }; handleFileChange(evt); } }}>
        <div style={{ fontSize: 24, marginBottom: 6 }}>📂</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>{file ? file.name : "Click or drag a CSV file here"}</div>
        <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>Only .csv files accepted</div>
        <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleFileChange} />
      </div>

      {/* Preview */}
      {preview && (
        <div style={{ background: "#F2F9E7", border: "1px solid #D1E8A0", borderRadius: 8, padding: "12px 16px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#3B6D11" }}>{preview.total} rows ready to import</div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>These will be appended to the <strong>{schema.label}</strong> table. Existing rows will not be changed.</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setPreview(null); setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
              style={{ padding: "8px 14px", borderRadius: 7, border: "1px solid #E2E8F0", background: "#fff", color: "#64748B", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button onClick={handleImport} disabled={importing}
              style={{ padding: "8px 18px", borderRadius: 7, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit", opacity: importing ? 0.7 : 1 }}>
              {importing ? "Importing…" : "Confirm Import"}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ background: result.failed > 0 ? "#FEF2F2" : "#F2F9E7", border: `1px solid ${result.failed > 0 ? "#FECACA" : "#D1E8A0"}`, borderRadius: 8, padding: "12px 16px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: result.failed > 0 ? "#DC2626" : "#3B6D11", marginBottom: 4 }}>
            {result.success > 0 && `✓ ${result.success} rows imported successfully`}
            {result.failed > 0 && ` · ✗ ${result.failed} rows failed`}
          </div>
          {result.errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: "#DC2626" }}>{e}</div>)}
        </div>
      )}
    </div>
  );
};

const AdminPage = ({ onSettingsChange }) => {
  const settings = useSettings();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    if (settings) setData({
      sources:     [...(settings.sources     || DEFAULT_SOURCES)],
      statuses:    [...(settings.statuses    || DEFAULT_STATUS)],
      warmth:      [...(settings.warmth      || DEFAULT_WARMTH)],
      teamMembers: [...(settings.teamMembers || DEFAULT_TEAM_MEMBERS)],
      products:    JSON.parse(JSON.stringify(settings.products    || DEFAULT_PRODUCTS)),
      components:  JSON.parse(JSON.stringify(settings.components  || DEFAULT_COMPONENTS)),
      tradeTypes:  [...(settings.tradeTypes || DEFAULT_TRADE_TYPES)],
    });
  }, [settings]);

  const saveKey = async (key, value) => {
    setSaving(key);
    await settingsApi.set(key, value);
    onSettingsChange();
    setSaving(null);
    setSaved(key);
    setTimeout(() => setSaved(null), 2000);
  };

  const updateSimple = (stateKey, i, val) => setData(d => ({ ...d, [stateKey]: d[stateKey].map((v, j) => j === i ? val : v) }));
  const addSimple = (stateKey) => setData(d => ({ ...d, [stateKey]: [...d[stateKey], ""] }));
  const removeSimple = (stateKey, i) => setData(d => ({ ...d, [stateKey]: d[stateKey].filter((_, j) => j !== i) }));

  const updateProducts = (action, i, j, val) => setData(d => {
    const p = JSON.parse(JSON.stringify(d.products));
    if (action === "label")         { p[i].label = val; }
    if (action === "supplier")      { p[i].suppliers[j] = val; }
    if (action === "addSupplier")   { p[i].suppliers.push(""); }
    if (action === "removeSupplier"){ p[i].suppliers.splice(j, 1); }
    if (action === "addProduct")    { p.push({ key: `product_${Date.now()}`, label: "", suppliers: ["Other"] }); }
    if (action === "removeProduct") { p.splice(i, 1); }
    return { ...d, products: p };
  });

  const updateComponents = (action, i, val) => setData(d => {
    const c = JSON.parse(JSON.stringify(d.components));
    if (action === "label")  { c[i].label = val; }
    if (action === "toggle") { const idx = c[i].products.indexOf(val); idx >= 0 ? c[i].products.splice(idx, 1) : c[i].products.push(val); }
    if (action === "remove") { c.splice(i, 1); }
    if (action === "add")    { c.push({ key: `comp_${Date.now()}`, label: "", products: [] }); }
    return { ...d, components: c };
  });

  if (!data) return <div style={{ color: "#94A3B8", padding: 40, textAlign: "center" }}>Loading settings…</div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Admin</div>
        <div style={{ fontSize: 14, color: "#64748B" }}>Manage dropdown lists and WIP configuration. Changes take effect immediately.</div>
      </div>
      <BackupExport />
      <CSVImport />
      <div className="ci-admin-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: "#94A3B8", textTransform: "uppercase", marginBottom: 12 }}>Lead Settings</div>
          <AdminSimpleList label="Lead Sources" items={data.sources} stateKey="sources" dbKey="sources" saving={saving} saved={saved}
            onUpdate={(i, v) => updateSimple("sources", i, v)} onAdd={() => addSimple("sources")} onRemove={(i) => removeSimple("sources", i)} onSave={() => saveKey("sources", data.sources)} />
          <AdminSimpleList label="Lead Statuses" items={data.statuses} stateKey="statuses" dbKey="statuses" saving={saving} saved={saved}
            onUpdate={(i, v) => updateSimple("statuses", i, v)} onAdd={() => addSimple("statuses")} onRemove={(i) => removeSimple("statuses", i)} onSave={() => saveKey("statuses", data.statuses)} />
          <AdminSimpleList label="Warmth Levels" items={data.warmth} stateKey="warmth" dbKey="warmth" allowBlank saving={saving} saved={saved}
            onUpdate={(i, v) => updateSimple("warmth", i, v)} onAdd={() => addSimple("warmth")} onRemove={(i) => removeSimple("warmth", i)} onSave={() => saveKey("warmth", data.warmth)} />
          <AdminSimpleList label="Team Members" items={data.teamMembers} stateKey="teamMembers" dbKey="team_members" saving={saving} saved={saved}
            onUpdate={(i, v) => updateSimple("teamMembers", i, v)} onAdd={() => addSimple("teamMembers")} onRemove={(i) => removeSimple("teamMembers", i)} onSave={() => saveKey("team_members", data.teamMembers)} />
          <AdminSimpleList label="Partner Trade Types" items={data.tradeTypes} stateKey="tradeTypes" dbKey="trade_types" saving={saving} saved={saved}
            onUpdate={(i, v) => updateSimple("tradeTypes", i, v)} onAdd={() => addSimple("tradeTypes")} onRemove={(i) => removeSimple("tradeTypes", i)} onSave={() => saveKey("trade_types", data.tradeTypes)} />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: "#94A3B8", textTransform: "uppercase", marginBottom: 12 }}>WIP Configuration</div>
          <AdminProductsEditor products={data.products} saving={saving} saved={saved} onSave={() => saveKey("products", data.products)} onUpdate={updateProducts} />
          <AdminComponentsEditor components={data.components} products={data.products || DEFAULT_PRODUCTS} saving={saving} saved={saved} onSave={() => saveKey("components", data.components)} onUpdate={updateComponents} />
        </div>
      </div>
    </div>
  );
};

const FollowUpPage = ({ leads, wipJobs = {}, partners = [], onSave, onViewLead }) => {
  // Find approved leads where a WIP job has components added but no order date
  const unorderedItems = [];
  leads.filter(l => l.status === "Approved").forEach(lead => {
    const j = wipJobs[lead.id];
    if (!j || !j.components) return;
    Object.entries(j.components).forEach(([k, val]) => {
      if (!val) return;
      if (Array.isArray(val)) {
        // New format: array of line items
        val.forEach(line => {
          // Count any line that exists (has id) and is not yet ordered or received
          if (line.id && !line.orderDate && !line.received) {
            unorderedItems.push({ lead, componentKey: k, line });
          }
        });
      }
      // Legacy single date string — already has order date, skip
    });
  });

  // Group by lead
  const byLead = {};
  unorderedItems.forEach(({ lead, componentKey, line }) => {
    if (!byLead[lead.id]) byLead[lead.id] = { lead, items: [] };
    byLead[lead.id].items.push({ componentKey, line });
  });
  const unorderedLeads = Object.values(byLead);

  const duePartners = partners.filter(p => p.next_contact && getContactUrgency(p.next_contact) !== null);

  // Shared section header
  const SectionHeader = ({ label, count, accentColor }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.2, textTransform: "uppercase" }}>{label}</div>
      <span style={{ background: accentColor + "22", color: accentColor, borderRadius: 99, fontSize: 11, fontWeight: 700, padding: "2px 8px" }}>{count}</span>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}>Follow Up</div>
        <div style={{ fontSize: 14, color: "#64748B", marginTop: 2 }}>Items requiring your attention</div>
      </div>

      {/* Components not yet ordered */}
      {unorderedLeads.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <SectionHeader label="Components Awaiting Order" count={unorderedItems.length} accentColor="#F59E0B" />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {unorderedLeads.map(({ lead, items }) => (
              <div key={lead.id} onClick={() => onViewLead(lead, "wip")}
                style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, transition: "opacity 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
                  <span style={{ background: "#FEF3C7", color: "#B45309", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 99, whiteSpace: "nowrap" }}>Unordered</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{lead.name}</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 1 }}>{items.map(({ componentKey, line }) => [line.item, line.supplier].filter(Boolean).join(" / ") || componentKey).join(", ")}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>WIP job</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#B45309" }}>{items.length} item{items.length > 1 ? "s" : ""}</div>
                  </div>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" style={{ color: "#94A3B8", flexShrink: 0 }}><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Partners to contact */}
      {duePartners.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <SectionHeader label="Partners to Contact" count={duePartners.length} accentColor="#4F46E5" />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {duePartners.map(p => {
              const urgency = getContactUrgency(p.next_contact);
              const isOverdue = urgency === "overdue";
              return (
                <div key={p.id}
                  style={{ background: isOverdue ? "#FEF2F2" : "#EEF2FF", border: `1px solid ${isOverdue ? "#FECACA" : "#C7D2FE"}`, borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, transition: "opacity 0.15s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                  onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
                    <span style={{ background: isOverdue ? "#FEE2E2" : "#E0E7FF", color: isOverdue ? "#DC2626" : "#4F46E5", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 99, whiteSpace: "nowrap" }}>{isOverdue ? "Overdue" : "Due Soon"}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{p.company_name || p.contact_name}</div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 1 }}>{p.trade_type}{p.suburb ? ` · ${p.suburb}` : ""}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>Next contact</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isOverdue ? "#DC2626" : "#0F172A" }}>{formatDate(p.next_contact)}</div>
                    </div>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" style={{ color: "#94A3B8", flexShrink: 0 }}><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Leads contact required */}
      <ContactRequired leads={leads} onSave={onSave} onViewLead={onViewLead} />
    </div>
  );
};


// ─── Completed Jobs Page ──────────────────────────────────────────────────────
const CompletedPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);

  useEffect(() => {
    completedJobsApi.getAll().then(data => {
      setJobs(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  }, []);

  const handleRevertToWip = async () => {
    if (!selected) return;
    setReverting(true);
    // Clear installed_at on wip_job
    if (selected.lead_id) {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/wip_jobs?lead_id=eq.${selected.lead_id}`, {
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
      });
      const wipData = await res.json();
      if (wipData?.[0]?.id) {
        await fetch(`${SUPABASE_URL}/rest/v1/wip_jobs?id=eq.${wipData[0].id}`, {
          method: "PATCH",
          headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json", Prefer: "return=representation" },
          body: JSON.stringify({ installed_at: null }),
        });
      }
    }
    // Delete from completed_jobs
    await completedJobsApi.delete(selected.id);
    if (selected.lead_id) {
      try { await activityApi.log(selected.lead_id, getCurrentUser(), "Reverted to WIP", selected.name || null); } catch (e) {}
    }
    setJobs(prev => prev.filter(j => j.id !== selected.id));
    setSelected(null);
    setConfirmRevert(false);
    setReverting(false);
  };

  const exportCSV = () => {
    const headers = ["Name","Phone","Email","Street","Suburb","Postcode","Invoice #","Invoice Value","Quote Value","Deposit Date","Install Date","Installed At"];
    const rows = jobs.map(j => [
      j.name, j.phone, j.email, j.street, j.suburb, j.postcode,
      j.invoice_number, j.invoice_value, j.quote_value,
      j.deposit_date, j.scheduled_install,
      j.installed_at ? new Date(j.installed_at).toLocaleDateString("en-AU") : ""
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${(v||"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `completed_jobs_${new Date().toISOString().split("T")[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totalValue = jobs.reduce((s, j) => s + (Number(j.invoice_value || j.quote_value) || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}>Completed Customers</div>
          <div style={{ fontSize: 14, color: "#64748B", marginTop: 4 }}>{jobs.length} completed jobs · {formatCurrency(totalValue)} total value</div>
        </div>
        <button onClick={exportCSV} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
          📥 Export CSV
        </button>
      </div>

      {loading ? <div style={{ textAlign: "center", padding: 60, color: "#94A3B8" }}>Loading…</div> : (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
            <thead>
              <tr style={{ background: "#F8FAFC" }}>
                {["Customer", "Invoice", "Value", "Products", "Components", "Deposit Date", "Install Date", "Installed"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.1, textTransform: "uppercase", borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>No completed jobs yet.</td></tr>
              ) : jobs.map((j, i) => {
                const activeProducts = Object.entries(j.products || {}).filter(([, v]) => v && v !== false);
                const componentLines = [];
                Object.entries(j.components || {}).forEach(([k, val]) => {
                  if (Array.isArray(val)) val.forEach(l => { if (l.received) componentLines.push(l.item || l.supplier || k); });
                  else if (val) componentLines.push(k);
                });
                return (
                  <tr key={j.id} onClick={() => setSelected(j)}
                    style={{ borderBottom: i < jobs.length - 1 ? "1px solid #F1F5F9" : "none", cursor: "pointer" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "13px 16px" }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{j.name}</div>
                      <div style={{ fontSize: 11, color: "#94A3B8" }}>{j.suburb || ""}</div>
                    </td>
                    <td style={{ padding: "13px 16px", fontSize: 13, color: "#475569", fontWeight: 600 }}>{j.invoice_number || "—"}</td>
                    <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 700, color: "#10B981" }}>{formatCurrency(j.invoice_value || j.quote_value)}</td>
                    <td style={{ padding: "13px 16px" }}>
                      {activeProducts.length === 0 ? <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span> :
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {activeProducts.map(([k, v]) => (
                            <span key={k} style={{ fontSize: 11, background: "#FFF7ED", color: "#C2410C", borderRadius: 5, padding: "2px 7px", fontWeight: 600 }}>{k}: {v}</span>
                          ))}
                        </div>}
                    </td>
                    <td style={{ padding: "13px 16px", fontSize: 12, color: "#64748B" }}>
                      {componentLines.length > 0 ? componentLines.slice(0, 3).join(", ") + (componentLines.length > 3 ? ` +${componentLines.length - 3}` : "") : "—"}
                    </td>
                    <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B", whiteSpace: "nowrap" }}>{formatDate(j.deposit_date)}</td>
                    <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B", whiteSpace: "nowrap" }}>{formatDate(j.scheduled_install)}</td>
                    <td style={{ padding: "13px 16px", fontSize: 13, color: "#10B981", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {j.installed_at ? new Date(j.installed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setSelected(null)}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 620, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", padding: "28px 32px" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}>{selected.name}</div>
                <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 2 }}>Installed {selected.installed_at ? new Date(selected.installed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setConfirmRevert(true)} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontWeight: 600, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>↩ Return to WIP</button>
                <button onClick={() => setSelected(null)} style={{ background: "#F1F5F9", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 18, color: "#64748B" }}>×</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              {[["Phone", selected.phone], ["Email", selected.email], ["Address", [selected.street, selected.suburb, selected.postcode].filter(Boolean).join(", ")], ["Invoice #", selected.invoice_number], ["Invoice Value", formatCurrency(selected.invoice_value)], ["Quote Value", formatCurrency(selected.quote_value)], ["Deposit Date", formatDate(selected.deposit_date)], ["Install Date", formatDate(selected.scheduled_install)]].map(([label, val]) => val ? (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1, marginBottom: 3 }}>{label.toUpperCase()}</div>
                  <div style={{ fontSize: 13, color: "#0F172A" }}>{val}</div>
                </div>
              ) : null)}
            </div>
            {selected.notes && (() => {
              const notes = typeof selected.notes === "string" ? (() => { try { return JSON.parse(selected.notes); } catch { return []; } })() : [];
              return notes.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1, marginBottom: 8 }}>NOTES</div>
                  {notes.map((n, i) => (
                    <div key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 14px", marginBottom: 6, fontSize: 13, color: "#334155" }}>
                      {n.author && <span style={{ fontWeight: 700, color: "#8DC73F", marginRight: 8 }}>{n.author}</span>}
                      {n.text}
                    </div>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {confirmRevert && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 400, width: "100%", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>↩</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>Return to WIP?</div>
            <div style={{ fontSize: 14, color: "#64748B", marginBottom: 24 }}>This will remove <strong>{selected?.name}</strong> from Completed and put them back into the WIP tab.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setConfirmRevert(false)} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
              <button onClick={handleRevertToWip} disabled={reverting} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#EF4444", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                {reverting ? "Reverting…" : "Yes, Return to WIP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Partner Modal ────────────────────────────────────────────────────────────
const PartnerModal = ({ partner, onClose, onSave, onDelete, isNew }) => {
  const { teamMembers: TEAM_MEMBERS, tradeTypes: TRADE_TYPES } = useSettings() || { teamMembers: DEFAULT_TEAM_MEMBERS, tradeTypes: DEFAULT_TRADE_TYPES };
  const [form, setForm] = useState({ ...partner });
  const [saving, setSaving] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [refClientName, setRefClientName] = useState("");
  const [refNote, setRefNote] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const hasChanges = JSON.stringify(form) !== JSON.stringify(partner);

  const handleClose = () => { if (hasChanges) setShowUnsaved(true); else onClose(); };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
    onClose();
  };

  const addNote = () => {
    const text = document.getElementById("partner-note-text").value.trim();
    const date = document.getElementById("partner-note-date").value;
    const author = document.getElementById("partner-note-author").value;
    if (!text) return;
    set("notes", [{ id: Date.now(), date, author, text }, ...(form.notes || [])]);
    document.getElementById("partner-note-text").value = "";
  };

  const logReferral = () => {
    if (!refClientName.trim()) return;
    const ref = { id: Date.now(), date: new Date().toISOString().split("T")[0], client: refClientName.trim(), note: refNote.trim(), user: getCurrentUser() };
    const newReferrals = [...(form.referrals || []), ref];
    setForm(f => ({ ...f, referrals: newReferrals, jobs_referred: newReferrals.length, last_contacted: ref.date }));
    setRefClientName(""); setRefNote(""); setShowReferral(false);
  };

  const sortedNotes = [...(form.notes || [])].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  const sortedReferrals = [...(form.referrals || [])].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={handleClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 900, height: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.22)" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 28px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#94A3B8", textTransform: "uppercase", marginBottom: 6 }}>{isNew ? "New Partner" : "Edit Partner"}</div>
            <input value={form.company_name || ""} onChange={e => set("company_name", e.target.value)} placeholder="Company name"
              style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", border: "none", outline: "none", width: "100%", fontFamily: "inherit", background: "transparent" }} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!isNew && <button onClick={() => { if (window.confirm("Delete this partner?")) { onDelete(form.id); onClose(); } }}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: "#EF4444", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>Delete</button>}
            <button onClick={handleClose} style={{ background: "#F1F5F9", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 18, color: "#64748B" }}>×</button>
          </div>
        </div>

        {/* Two column body */}
        <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", flex: 1, overflow: "hidden" }}>

          {/* LEFT */}
          <div style={{ overflowY: "auto", borderRight: "1px solid #F1F5F9", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div><label style={labelStyle}>Contact Name</label><input value={form.contact_name || ""} onChange={e => set("contact_name", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Trade Type</label>
                <select value={form.trade_type || ""} onChange={e => set("trade_type", e.target.value)} style={inputStyle}>
                  {(TRADE_TYPES || DEFAULT_TRADE_TYPES).map(t => <option key={t}>{t}</option>)}
                </select></div>
              <div><label style={labelStyle}>Phone</label><input value={form.phone || ""} onChange={e => set("phone", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Email</label><input value={form.email || ""} onChange={e => set("email", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Suburb</label><input value={form.suburb || ""} onChange={e => set("suburb", e.target.value)} style={inputStyle} /></div>
              <div style={{ gridColumn: "1/-1" }}><label style={labelStyle}>Office Address</label>
                <input value={form.office_address || ""} onChange={e => set("office_address", e.target.value)} style={inputStyle} placeholder="e.g. 42 Example St, Suburb VIC 3000" /></div>
              <div><label style={labelStyle}>Jobs Referred</label>
                <div style={{ ...inputStyle, background: "#F8FAFC", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 18, color: "#8DC73F" }}>{form.jobs_referred || 0}</span>
                  <span style={{ fontSize: 12, color: "#94A3B8" }}>referrals</span>
                </div></div>
              <div><label style={labelStyle}>Last Contacted</label><input type="date" value={form.last_contacted || ""} onChange={e => set("last_contacted", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Next Contact</label><input type="date" value={form.next_contact || ""} onChange={e => set("next_contact", e.target.value)} style={inputStyle} /></div>
            </div>

            {/* Referral log */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1, textTransform: "uppercase" }}>Referral Log</div>
                <button onClick={() => setShowReferral(true)} style={{ padding: "5px 12px", borderRadius: 7, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>+ Log Referral</button>
              </div>
              {sortedReferrals.length === 0 ? (
                <div style={{ fontSize: 13, color: "#CBD5E1", padding: "8px 0" }}>No referrals logged yet</div>
              ) : sortedReferrals.map(r => (
                <div key={r.id} style={{ background: "#F7FBF0", border: "1px solid #D1E8A0", borderRadius: 8, padding: "8px 12px", marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{r.client}</span>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>{r.date ? new Date(r.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                  </div>
                  {r.note && <div style={{ fontSize: 12, color: "#64748B" }}>{r.note}</div>}
                  {r.user && <div style={{ fontSize: 11, color: "#8DC73F", fontWeight: 600, marginTop: 2 }}>{r.user}</div>}
                </div>
              ))}
            </div>

            <button onClick={handleSave} disabled={saving} style={{ padding: "11px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", opacity: saving ? 0.7 : 1, fontFamily: "inherit" }}>
              {saving ? "Saving…" : "Save Partner"}
            </button>
          </div>

          {/* RIGHT — notes */}
          <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px 12px", borderBottom: "1px solid #F1F5F9", fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase", flexShrink: 0 }}>
              Notes {sortedNotes.length > 0 && <span style={{ color: "#CBD5E1", fontWeight: 400 }}>· {sortedNotes.length}</span>}
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
              {sortedNotes.length === 0 && <div style={{ textAlign: "center", color: "#CBD5E1", fontSize: 14, marginTop: 40 }}>No notes yet</div>}
              {sortedNotes.map(note => (
                <div key={note.id} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: "12px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 10 }}>
                      {note.author && <span style={{ fontSize: 12, fontWeight: 700, color: "#8DC73F" }}>{note.author}</span>}
                      <span style={{ fontSize: 12, color: "#94A3B8" }}>{note.date ? new Date(note.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
                    </div>
                    <button onClick={() => set("notes", (form.notes || []).filter(n => n.id !== note.id))} style={{ background: "none", border: "none", cursor: "pointer", color: "#CBD5E1", fontSize: 15, padding: 0 }}>×</button>
                  </div>
                  <div style={{ fontSize: 14, color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{note.text}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: "14px 24px 18px", borderTop: "1px solid #F1F5F9", flexShrink: 0, background: "#fff" }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input type="date" id="partner-note-date" defaultValue={new Date().toISOString().split("T")[0]} style={{ ...inputStyle, width: 145, flexShrink: 0 }} />
                <select id="partner-note-author" style={{ ...inputStyle, width: 110, flexShrink: 0 }}>
                  {TEAM_MEMBERS.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <textarea id="partner-note-text" placeholder="Write a note…" rows={3}
                  style={{ ...inputStyle, flex: 1, resize: "none", lineHeight: 1.5 }}
                  onKeyDown={e => { if (e.key === "Enter" && e.metaKey) { addNote(); } }} />
                <button onClick={addNote} style={{ padding: "10px 16px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, alignSelf: "flex-end", height: 42 }}>+ Add</button>
              </div>
            </div>
          </div>
        </div>

        {/* Unsaved warning */}
        {showUnsaved && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>Unsaved changes</div>
              <div style={{ fontSize: 14, color: "#64748B", marginBottom: 24 }}>You have unsaved changes. What would you like to do?</div>
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button onClick={() => setShowUnsaved(false)} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Keep Editing</button>
                <button onClick={() => { setShowUnsaved(false); onClose(); }} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: "#EF4444", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Discard</button>
                <button onClick={() => { setShowUnsaved(false); handleSave(); }} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Save & Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Log Referral popup */}
        {showReferral && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 400, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Log a Referral</div>
              <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}>Record a client referred by {form.company_name || "this partner"}</div>
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Client Name *</label>
                <input value={refClientName} onChange={e => setRefClientName(e.target.value)} placeholder="e.g. Smith Residence" style={inputStyle} autoFocus /></div>
              <div style={{ marginBottom: 20 }}><label style={labelStyle}>Note (optional)</label>
                <input value={refNote} onChange={e => setRefNote(e.target.value)} placeholder="e.g. Kitchen blinds, Toorak" style={inputStyle} /></div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => { setShowReferral(false); setRefClientName(""); setRefNote(""); }} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
                <button onClick={logReferral} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Log Referral</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Partners Page ────────────────────────────────────────────────────────────
const PartnersPage = () => {
  const { tradeTypes: TRADE_TYPES_SETTING } = useSettings() || { tradeTypes: DEFAULT_TRADE_TYPES };
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");

  const load = async () => {
    const data = await partnersApi.getAll();
    setPartners(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const savePartner = async (form) => {
    const cleaned = { ...form };
    ["last_contacted", "next_contact"].forEach(k => { if (!cleaned[k]) cleaned[k] = null; });
    if (Array.isArray(cleaned.notes)) cleaned.notes = JSON.stringify(cleaned.notes);
    if (Array.isArray(cleaned.referrals)) cleaned.referrals = JSON.stringify(cleaned.referrals);
    if (form.id) { await partnersApi.update(form.id, cleaned); }
    else { await partnersApi.insert(cleaned); }
    await load();
  };

  const deletePartner = async (id) => {
    await partnersApi.delete(id);
    await load();
  };

  const filtered = partners.filter(p => {
    const matchSearch = !search || [p.company_name, p.contact_name, p.suburb].some(v => v?.toLowerCase().includes(search.toLowerCase()));
    const matchType = filterType === "All" || p.trade_type === filterType;
    return matchSearch && matchType;
  });

  const tradeTypes = ["All", ...(TRADE_TYPES_SETTING || DEFAULT_TRADE_TYPES)];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}>Partners</div>
          <div style={{ fontSize: 14, color: "#64748B", marginTop: 2 }}>Trade relationships and referral contacts</div>
        </div>
        <button onClick={() => setShowAdd(true)} style={{ background: "#8DC73F", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Partner
        </button>
      </div>

      {/* Search + filter */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search partners…"
          style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 14, background: "#fff", outline: "none", minWidth: 220, color: "#0F172A" }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {tradeTypes.map(t => (
            <button key={t} onClick={() => setFilterType(t)}
              style={{ padding: "7px 14px", borderRadius: 20, border: "1px solid", fontSize: 13, fontWeight: 600, cursor: "pointer",
                borderColor: filterType === t ? "#8DC73F" : "#E2E8F0",
                background: filterType === t ? "#F2F9E7" : "#fff",
                color: filterType === t ? "#8DC73F" : "#64748B", fontFamily: "inherit" }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div style={{ textAlign: "center", padding: 60, color: "#94A3B8" }}>Loading…</div> : (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
            <thead>
              <tr style={{ background: "#F8FAFC" }}>
                {["Company", "Contact", "Trade Type", "Suburb", "Jobs Referred", "Last Contacted", "Next Contact"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.1, textTransform: "uppercase", borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>No partners found.</td></tr>
              ) : filtered.map((p, i) => {
                const urgency = getContactUrgency(p.next_contact);
                return (
                  <tr key={p.id} onClick={() => setSelected(p)}
                    style={{ borderBottom: i < filtered.length - 1 ? "1px solid #F1F5F9" : "none", cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "13px 16px", fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{p.company_name || "—"}</td>
                    <td style={{ padding: "13px 16px", fontSize: 13, color: "#475569" }}>{p.contact_name || "—"}</td>
                    <td style={{ padding: "13px 16px" }}>
                      {p.trade_type && <span style={{ fontSize: 11, background: "#EEF2FF", color: "#4F46E5", borderRadius: 5, padding: "2px 8px", fontWeight: 600 }}>{p.trade_type}</span>}
                    </td>
                    <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B" }}>{p.suburb || "—"}</td>
                    <td style={{ padding: "13px 16px" }}>
                      {p.jobs_referred > 0 ? <span style={{ fontSize: 13, fontWeight: 700, color: "#8DC73F" }}>{p.jobs_referred}</span> : <span style={{ color: "#CBD5E1", fontSize: 13 }}>0</span>}
                    </td>
                    <td style={{ padding: "13px 16px", fontSize: 13, color: "#64748B", whiteSpace: "nowrap" }}>{formatDate(p.last_contacted)}</td>
                    <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                      {p.next_contact ? (
                        <span style={{ fontSize: 13, fontWeight: urgency ? 700 : 400, color: urgency === "overdue" ? "#EF4444" : urgency ? "#F59E0B" : "#64748B" }}>
                          {formatDate(p.next_contact)}
                        </span>
                      ) : <span style={{ color: "#CBD5E1", fontSize: 13 }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 13, color: "#94A3B8" }}>Showing {filtered.length} of {partners.length} partners</div>

      {selected && <PartnerModal partner={selected} onClose={() => { setSelected(null); load(); }} onSave={savePartner} onDelete={deletePartner} isNew={false} />}
      {showAdd && <PartnerModal partner={emptyPartner()} onClose={() => { setShowAdd(false); load(); }} onSave={savePartner} onDelete={() => {}} isNew={true} />}
    </div>
  );
};

const NAV_ITEMS = ["Dashboard", "Partners", "Leads", "WIP", "Completed"];

const NavBar = ({ active, onChange, followUpCount, isAdmin }) => (
  <div className="ci-nav" style={{ background: "#fff", borderBottom: "1px solid #E2E8F0", padding: "0 32px" }}>
    <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", gap: 0 }}>
      {[...NAV_ITEMS, ...(isAdmin ? ["Admin"] : [])].map((item) => (
        <button key={item} onClick={() => onChange(item)}
          style={{
            padding: "14px 20px", background: "none", border: "none", cursor: "pointer",
            fontSize: 14, fontWeight: 600, fontFamily: "inherit",
            color: active === item ? "#8DC73F" : "#64748B",
            borderBottom: active === item ? "2px solid #8DC73F" : "2px solid transparent",
            marginBottom: -1, transition: "all 0.15s",
          }}>
          {item}
        </button>
      ))}
      {followUpCount > 0 && (
        <button onClick={() => onChange("Follow Up")}
          style={{
            padding: "14px 20px", background: "none", border: "none", cursor: "pointer",
            fontSize: 14, fontWeight: 800, fontFamily: "inherit",
            color: active === "Follow Up" ? "#F97316" : "#EA580C",
            borderBottom: active === "Follow Up" ? "2px solid #F97316" : "2px solid transparent",
            marginBottom: -1, transition: "all 0.15s",
            display: "flex", alignItems: "center", gap: 7,
          }}>
          🔔 Follow Up
          <span style={{ background: "#FEF3C7", color: "#B45309", fontSize: 11, fontWeight: 800, borderRadius: 99, padding: "2px 7px", border: "1px solid #FDE68A" }}>
            {followUpCount}
          </span>
        </button>
      )}
    </div>
  </div>
);

export default function App() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [wipJobs, setWipJobsApp] = useState({});
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [page, setPage] = useState("Dashboard");
  const [error, setError] = useState(null);
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("ci_user") && !!sessionStorage.getItem("ci_authed"));
  const [isAdmin, setIsAdmin] = useState(sessionStorage.getItem("ci_admin") === "1");
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);
  const [nameInput, setNameInput] = useState(getCurrentUser() || "");
  const [nameError, setNameError] = useState(false);
  const [settings, setSettings] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const handleLogin = () => {
    if (!nameInput) { setNameError(true); return; }
    if (pwInput === APP_PASSWORD || pwInput === ADMIN_PASSWORD) {
      setCurrentUser(nameInput);
      sessionStorage.setItem("ci_authed", "1");
      sessionStorage.setItem("ci_admin", pwInput === ADMIN_PASSWORD ? "1" : "0");
      setIsAdmin(pwInput === ADMIN_PASSWORD);
      setAuthed(true); setPwError(false); setNameError(false);
    } else { setPwError(true); }
  };

  const loadSettings = async () => {
    const raw = await settingsApi.getAll();
    setSettings({
      sources:     raw.sources     || DEFAULT_SOURCES,
      statuses:    raw.statuses    || DEFAULT_STATUS,
      warmth:      raw.warmth      || DEFAULT_WARMTH,
      teamMembers: raw.team_members || DEFAULT_TEAM_MEMBERS,
      products:    raw.products    || DEFAULT_PRODUCTS,
      components:  raw.components  || DEFAULT_COMPONENTS,
      tradeTypes:  raw.trade_types  || DEFAULT_TRADE_TYPES,
    });
  };

  const loadLeads = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await supabase.getAll();
      if (Array.isArray(data)) setLeads(data);
      else if (!silent) setError("Could not load leads. Check your Supabase connection.");
    } catch (e) {
      if (!silent) setError("Connection error. Check your environment variables.");
    }
    if (!silent) { setLoading(false); setInitialLoad(false); }
    setLastRefreshed(new Date());
  };

  const [newWipAlert, setNewWipAlert] = useState(null);
  const [partnersForFollowUp, setPartnersForFollowUp] = useState([]);

  useEffect(() => {
    partnersApi.getAll().then(data => setPartnersForFollowUp(data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const isEditingRef = React.useRef(false);
  const seenWipRef = React.useRef(new Set());

  useEffect(() => {
    loadLeads();
    loadSettings();
    wipApi.getAll().then((jobs) => {
      const map = {};
      (jobs || []).forEach((j) => {
        map[j.lead_id] = j;
        seenWipRef.current.add(j.lead_id);
      });
      setWipJobsApp(map);
    });
    const onFocusIn = (e) => { if (["INPUT","TEXTAREA","SELECT"].includes(e.target.tagName)) isEditingRef.current = true; };
    const onFocusOut = (e) => { if (["INPUT","TEXTAREA","SELECT"].includes(e.target.tagName)) isEditingRef.current = false; };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    const interval = setInterval(() => {
      if (isEditingRef.current) return;
      loadLeads(true);
      wipApi.getAll().then((jobs) => {
        const map = {};
        const newLeads = [];
        (jobs || []).forEach((j) => {
          map[j.lead_id] = j;
          if (!seenWipRef.current.has(j.lead_id) && !j.installed_at) newLeads.push(j.lead_id);
          seenWipRef.current.add(j.lead_id);
        });
        setWipJobsApp(map);
        if (newLeads.length > 0) setNewWipAlert(newLeads.length);
      });
    }, 30000);
    return () => { clearInterval(interval); document.removeEventListener("focusin", onFocusIn); document.removeEventListener("focusout", onFocusOut); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveLead = async (form, prevForm) => {
    const user = getCurrentUser();
    try {
      if (form.id) {
        await supabase.update(form.id, form);
        try {
          if (prevForm && prevForm.status !== form.status) {
            await activityApi.log(form.id, user, "Status changed", `${prevForm.status} → ${form.status}`);
          }
          if (prevForm) {
            const changes = [];
            if (prevForm.warmth !== form.warmth && form.warmth) changes.push(`Warmth: ${form.warmth}`);
            if (prevForm.next_contact !== form.next_contact && form.next_contact) changes.push(`Next contact: ${form.next_contact}`);
            if (prevForm.quote_value !== form.quote_value && form.quote_value) changes.push(`Quote value: $${form.quote_value}`);
            if (prevForm.quote_sent_date !== form.quote_sent_date && form.quote_sent_date) changes.push(`Quote sent: ${form.quote_sent_date}`);
            if (prevForm.source !== form.source && form.source) changes.push(`Source: ${form.source}`);
            if (prevForm.invoice_number !== form.invoice_number && form.invoice_number) changes.push(`Invoice #: ${form.invoice_number}`);
            if (prevForm.invoice_value !== form.invoice_value && form.invoice_value) changes.push(`Invoice value: $${form.invoice_value}`);
            if (prevForm.approved_date !== form.approved_date && form.approved_date) changes.push(`Approved: ${form.approved_date}`);
            if (changes.length > 0) {
              await activityApi.log(form.id, user, "Lead updated", changes.join(" · "));
            } else if (prevForm.status === form.status) {
              await activityApi.log(form.id, user, "Lead updated", null);
            }
          } else {
            await activityApi.log(form.id, user, "Lead updated", null);
          }
        } catch (e) { console.warn("Activity log failed:", e); }
      } else {
        const result = await supabase.insert(form);
        const newId = Array.isArray(result) ? result[0]?.id : result?.id;
        if (newId) {
          try { await activityApi.log(newId, user, "Lead created", form.name); } catch (e) { console.warn("Activity log failed:", e); }
          if (form.status === "Approved") {
            try { await wipApi.upsert({ ...emptyWipJob(newId), deposit_date: form.approved_date || null }); } catch (e) { console.warn("WIP create failed:", e); }
          }
        }
      }
    } catch (e) {
      console.error("Save lead failed:", e);
    }
    await loadLeads();
  };

  const manualRefresh = async () => {
    await loadLeads(true);
    wipApi.getAll().then((jobs) => {
      const map = {};
      (jobs || []).forEach((j) => { map[j.lead_id] = j; });
      setWipJobsApp(map);
    });
  };

  const deleteLead = async (id, name) => {
    await activityApi.log(id, getCurrentUser(), "Lead deleted", name);
    await supabase.delete(id);
    await loadLeads();
  };

  const importLeads = async (leadsToImport) => {
    setLoading(true);
    for (const lead of leadsToImport) {
      await supabase.insert(lead);
    }
    await loadLeads();
  };

  if (!authed) return (
    <div style={{ minHeight: "100vh", background: "#1C2E0F", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "40px 44px", width: "100%", maxWidth: 380, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", textAlign: "center" }}>
        <img src="/favicon.png" alt="Curtain Ideas" style={{ width: 64, height: 64, borderRadius: 12, marginBottom: 20 }} />
        <div style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Curtain Ideas</div>
        <div style={{ fontSize: 14, color: "#94A3B8", marginBottom: 28 }}>Lead Tracker — Team Access</div>
        <div style={{ textAlign: "left", marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 5 }}>Who are you?</label>
          <select value={nameInput} onChange={(e) => { setNameInput(e.target.value); setNameError(false); }}
            style={{ width: "100%", padding: "11px 14px", borderRadius: 8, border: nameError ? "1px solid #EF4444" : "1px solid #E2E8F0", fontSize: 15, fontFamily: "inherit", boxSizing: "border-box", outline: "none", background: "#fff" }}>
            <option value="">— Select your name —</option>
            {DEFAULT_TEAM_MEMBERS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {nameError && <div style={{ color: "#EF4444", fontSize: 12, marginTop: 4 }}>Please select your name</div>}
        </div>
        <div style={{ textAlign: "left", marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 5 }}>Password</label>
          <input type="password" placeholder="Enter password" value={pwInput}
            onChange={(e) => { setPwInput(e.target.value); setPwError(false); }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            style={{ width: "100%", padding: "11px 14px", borderRadius: 8, border: pwError ? "1px solid #EF4444" : "1px solid #E2E8F0", fontSize: 15, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }} />
          {pwError && <div style={{ color: "#EF4444", fontSize: 12, marginTop: 4 }}>Incorrect password. Try again.</div>}
        </div>
        <button onClick={handleLogin}
          style={{ width: "100%", padding: "11px", borderRadius: 8, border: "none", background: "#8DC73F", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 4 }}>
          Login
        </button>
      </div>
    </div>
  );

  return (
    <SettingsContext.Provider value={settings}>
    <div style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <div className="ci-topbar" style={{ background: "#1C2E0F", padding: "0 32px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 58 }}>
          {/* Left — refresh */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={manualRefresh} title="Refresh" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 7, padding: "5px 10px", cursor: "pointer", color: "#fff", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
              ↻
            </button>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>
              {lastRefreshed.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          </div>
          {/* Centre — title */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
            <img src="/favicon.png" alt="Curtain Ideas" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover" }} />
            <span style={{ fontWeight: 700, fontSize: 16, color: "#fff", letterSpacing: -0.3 }}>Curtain Ideas Lead Tracker</span>
          </div>
          {/* Right — greeting */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#8DC73F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
              {getCurrentUser()?.charAt(0) || "?"}
            </div>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>
              Hi, {getCurrentUser() || "Guest"}
            </span>
          </div>
        </div>
      </div>
      <NavBar active={page} onChange={setPage} isAdmin={isAdmin} followUpCount={(() => {
        const contactDue = leads.filter(l =>
          ["In Progress","Delayed","Uncontactable"].includes(l.status) &&
          l.next_contact && getContactUrgency(l.next_contact) !== null
        ).length;
        const unordered = leads.filter(l => l.status === "Approved").reduce((count, lead) => {
          const j = wipJobs[lead.id];
          if (!j || !j.components) return count;
          return count + Object.values(j.components).reduce((c, val) => {
            if (!Array.isArray(val)) return c;
            return c + val.filter(line => line.id && (line.supplier || line.item) && !line.orderDate && !line.received).length;
          }, 0);
        }, 0);
        const partnersDue = partnersForFollowUp.filter(p => p.next_contact && getContactUrgency(p.next_contact) !== null).length;
        return contactDue + unordered + partnersDue;
      })()} />
      {newWipAlert && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: "#1C2E0F", color: "#fff", borderRadius: 12, padding: "14px 20px", zIndex: 9999, boxShadow: "0 8px 32px rgba(0,0,0,0.25)", display: "flex", alignItems: "center", gap: 12, maxWidth: 320 }}>
          <span style={{ fontSize: 20 }}>🔔</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{newWipAlert} new WIP job{newWipAlert > 1 ? "s" : ""}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>New customer{newWipAlert > 1 ? "s" : ""} added to WIP</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button onClick={() => { setPage("WIP"); setNewWipAlert(null); }} style={{ background: "#8DC73F", border: "none", borderRadius: 7, padding: "5px 12px", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>View WIP</button>
            <button onClick={() => setNewWipAlert(null)} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 7, padding: "5px 12px", color: "#fff", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Dismiss</button>
          </div>
        </div>
      )}
      <div className="ci-page-wrap" style={{ maxWidth: 1300, margin: "0 auto", padding: "28px 32px" }}>
        {error && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "14px 18px", color: "#DC2626", marginBottom: 20, fontSize: 14 }}>{error}</div>}
        {loading && initialLoad ? (
          <div style={{ textAlign: "center", padding: 80, color: "#94A3B8", fontSize: 15 }}>Loading…</div>
        ) : (
          <>
            {page === "Dashboard" && <DashboardPage leads={leads} onSaveLead={saveLead} onViewLead={(lead) => { setPage("Leads"); setSelected(lead); }} />}
            {page === "Follow Up" && <FollowUpPage leads={leads} wipJobs={wipJobs} partners={partnersForFollowUp} onSave={saveLead} onViewLead={(lead, dest) => { if (dest === "wip") { setPage("WIP"); } else { setPage("Leads"); setSelected(lead); } }} />}
            {page === "Leads" && <LeadsPage leads={leads} onSelectLead={setSelected} onAddLead={() => setShowAdd(true)} onImport={importLeads} />}
            {page === "WIP" && <WIPPage leads={leads} onWipJobsChange={(map) => setWipJobsApp(map)} />}
            {page === "Completed" && <CompletedPage />}
            {page === "Partners" && <PartnersPage />}
            {page === "Admin" && isAdmin && <AdminPage onSettingsChange={loadSettings} />}
          </>
        )}
      </div>
      {selected && <Modal lead={selected} onClose={() => setSelected(null)} onSave={saveLead} onDelete={deleteLead} isNew={false} />}
      {showAdd && <Modal lead={emptyLead()} onClose={() => setShowAdd(false)} onSave={saveLead} onDelete={() => {}} isNew={true} />}
    </div>
    </SettingsContext.Provider>
  );
}